/**
 * Balloon tool mixin
 * Provides methods for creating, editing, and managing speech balloons.
 */
import { markRaw } from 'vue';
import paper from 'paper';


export default {
    data() {
        return {
            active_balloon_type: 'speech',
            balloon_preview: null,
            balloon_default_fill: '#FFFFFF',   // White background as default fill color
            balloon_default_stroke: '#000000', // Black border as default stroke color
            balloon_default_stroke_width: 2,   // Temporary default - overridden by preferences.balloon_defaults.stroke_width
            balloon_default_stroke_dash: '', // Default border dash (empty = solid)
            balloon_fill_alpha: 1,            // Default fill opacity (fully opaque)

            selected_object_shape_stroke_width: 2, // Temporary default - updated from selected object or preferences           
            
            active_node_type: 'smooth',       // Default node type (smooth or corner)
            balloon_edit_mode_active: false,  // Track if any balloon is in edit mode
            editing_balloon: null,      // Currently editing balloon reference
            
            //! Pointers
            active_pointer_type: 'triangle', // Default pointer type for the stage
            pointer_width: 12,          // Default width for triangle pointers
            curved_auto_edit_mode: false, // Automatically enter edit mode after adding curved pointer
            curved_reflect_mode: false, // Reflect curvature side (default: auto-reflect on left side)
            zigzag_reflect_mode: false, // Reflect zigzag displacement (default: auto-reflect based on position)
            thought_bubble_count: 3,    // Number of thought bubbles
            thought_bubble_size: 18,    // Size of the initial thought bubble
            thought_merge_overlap: true, // Merge overlapping thought bubbles
            thought_balloon_simplicity: 0.1, // Simplicity level (0-1): 0=complex (80), 1=simple (120)
            active_text_index_in_merged_balloon: null, // Track the active text node in merged balloons
        
            // Balloon toolbar position
            balloon_toolbar_position: {
                x: 0,
                y: 0
            },
            selected_node_index: null,  // Index of currently selected anchor for CP editing
            color_update_timer: null,    // Timer for throttling fill updates
            stroke_update_timer: null,   // Timer for throttling stroke updates
            alpha_update_timer: null,    // Timer for throttling alpha updates
            toolbar_update_timer: null,  // Timer for throttling toolbar updates
            history_pending_balloon_edit: false, // Flag to batch history for node edits
            history_timer_balloon_edit: null, // Timer for batching history
            _cp_drag_update_pending: false, // Flag for CP drag animation frame
            _last_cp_drag_event: null,    // Store last CP drag event data
            _is_adding_node: false, // Add this flag if not already present
            _cp_drag_start_positions: null, // To store original CP positions on drag start
        };
    },

    computed: {
        // Sort texts by height for merged balloons (top texts first)
        selected_object_texts_sorted() {
            if (!this.selected_object || !this.selected_object.texts) {
                return [];
            }
            
            // Create array with text nodes and their original indices
            const texts_with_indices = this.selected_object.texts.map((text_node, original_index) => ({
                text_node,
                original_index,
                y: text_node.y()
            }));
            
            // Sort by Y position (ascending = top to bottom)
            return texts_with_indices.sort((a, b) => a.y - b.y);
        },
    },

    methods: {        

        initialize_balloon_defaults() {
            if (this.preferences.balloon_defaults) {
                this.balloon_default_fill = this.preferences.balloon_defaults.fill;
                this.balloon_default_stroke = this.preferences.balloon_defaults.stroke;
                this.balloon_default_stroke_width = this.preferences.balloon_defaults.stroke_width;
                this.balloon_default_stroke_dash = this.preferences.balloon_defaults.stroke_dash;
                this.balloon_fill_alpha = this.preferences.balloon_defaults.alpha;
            }
        },

        async get_line_shape_intersection_local(drag_start_local, drag_end_local, balloon_shape_data_string) {
            // Ensure Paper.js is loaded and accessible via global 'paper'
            if (typeof paper === 'undefined' || !paper.PaperScope) {
                console.error('Paper.js not found. Cannot calculate intersection.');
                return { intersection_point: null, started_inside: false, normal_at_intersection: null };
            }
        
            let intersection_data = { intersection_point: null, started_inside: false, normal_at_intersection: null }; // Initialize normal_at_intersection
            const original_project = paper.project; // Save current project
            // Create and activate new project on an offscreen canvas
            const temp_canvas = document.createElement('canvas');
            const temp_project = new paper.Project(temp_canvas); 
        
            try {
                const balloon_path = new paper.Path(balloon_shape_data_string);
                if (!balloon_path || balloon_path.isEmpty()) {
                    console.warn('Could not create balloon path in Paper.js or path is empty.');
                } else {
                    const p_start = new paper.Point(drag_start_local.x, drag_start_local.y);
                    const p_end = new paper.Point(drag_end_local.x, drag_end_local.y);
            
                    intersection_data.started_inside = balloon_path.contains(p_start);
            
                    const guide_line = new paper.Path.Line(p_start, p_end);
                    const intersections = guide_line.getIntersections(balloon_path);
            
                    if (intersections.length > 0) {
                        // Find the intersection closest to p_start (drag_start_local).
                        // This works for both inward and outward drags as it's the first hit.
                        let closest_intersection = intersections[0];
                        let min_dist_sq = p_start.getDistance(intersections[0].point, true);
            
                        for (let i = 1; i < intersections.length; i++) {
                            const dist_sq = p_start.getDistance(intersections[i].point, true);
                            if (dist_sq < min_dist_sq) {
                                min_dist_sq = dist_sq;
                                closest_intersection = intersections[i];
                            }
                        }
                        intersection_data.intersection_point = {
                            x: closest_intersection.point.x,
                            y: closest_intersection.point.y
                        };
                        // Add normal vector at the intersection point
                        if (closest_intersection.normal) {
                            intersection_data.normal_at_intersection = {
                                x: closest_intersection.normal.x,
                                y: closest_intersection.normal.y
                            };
                        }
                    }
                    // Cleanup paths from temp_project's active layer
                    balloon_path.remove();
                    guide_line.remove();
                }
            } catch (error) {
                console.error('Error during Paper.js intersection calculation:', error);
            } finally {
                if (temp_project) {
                    temp_project.remove(); // Remove the temporary project
                }
                if (original_project) {
                    original_project.activate(); // Restore the original project
                }
            }
            return intersection_data;
        },

        generate_zigzag_svg_path(base1, base2, endpoint, pointer_width, balloon_center = null) {
            // Calculate direction and length from center of base points to endpoint
            const center_x = (base1.x + base2.x) / 2;
            const center_y = (base1.y + base2.y) / 2;
            const dir_x = endpoint.x - center_x;
            const dir_y = endpoint.y - center_y;
            const length = Math.sqrt(dir_x * dir_x + dir_y * dir_y);
            
            if (length < 0.001) {
                console.warn('Points are too close to generate zigzag path');
                return null;
            }
            
            // Normalize direction vector
            const norm_x = dir_x / length;
            const norm_y = dir_y / length;
            
            // Calculate perpendicular vector for zigzag width
            const perp_x = -norm_y;
            const perp_y = norm_x;
            
            // Determine inversion based on position relative to balloon center
            let should_invert = false;
            if (balloon_center) {
                const is_on_left_side = center_x < balloon_center.x;
                const is_on_top_side = center_y < balloon_center.y;
                
                // Double inversion: left side inverts, top side inverts
                // So: top-left = no inversion (double negative), others = single inversion
                const horizontal_invert = is_on_left_side;
                const vertical_invert = is_on_top_side;
                should_invert = horizontal_invert !== vertical_invert; // XOR logic
                
                // Apply manual reflect mode override
                if (this.zigzag_reflect_mode) {
                    should_invert = !should_invert;
                }
            }
            
            // Apply inversion to perpendicular displacement
            const invert_factor = should_invert ? -1 : 1;
            
            // Zigzag parameters
            const zigzag_width = pointer_width * 0.8;
            const segment_length = length / 3;
            const internal_segment_length = segment_length * 1.85;

            var displacement = 22;
            if (segment_length > displacement) {
                displacement = segment_length; 
            } 

            const pair_displacement = displacement;
            const pair_displacement2 = displacement;

            //console.log("segment length :", segment_length);
            
            // Generate 5 points in correct order for zigzag pattern
            // Apply inversion factor to perpendicular displacements
            const zigzag_points = [
                // zigzag1 (first point on one side - connects from base1)
                {
                    x: center_x + norm_x * segment_length - perp_x * zigzag_width - perp_x * pair_displacement * invert_factor,
                    y: center_y + norm_y * segment_length - perp_y * zigzag_width - perp_y * pair_displacement * invert_factor,
                    type: 'main',
                    node_type: 'corner'
                },
                // zigzag3 (second point on same side as zigzag1 - continues from zigzag1)
                {
                    x: center_x + norm_x * internal_segment_length - perp_x * zigzag_width + perp_x * pair_displacement * invert_factor,
                    y: center_y + norm_y * internal_segment_length - perp_y * zigzag_width + perp_y * pair_displacement * invert_factor,
                    type: 'main',
                    node_type: 'corner'
                },
                // endpoint (final destination from zigzag3)
                {
                    x: endpoint.x,
                    y: endpoint.y,
                    type: 'main',
                    node_type: 'corner'
                },
                // zigzag4 (second point on opposite side - connects to endpoint)
                {
                    x: center_x + norm_x * internal_segment_length + perp_x * zigzag_width + perp_x * pair_displacement2 * invert_factor,
                    y: center_y + norm_y * internal_segment_length + perp_y * zigzag_width + perp_y * pair_displacement2 * invert_factor,
                    type: 'main',
                    node_type: 'corner'
                },
                // zigzag2 (first point on opposite side - connects from base2)
                {
                    x: center_x + norm_x * segment_length + perp_x * zigzag_width - perp_x * pair_displacement2 * invert_factor,
                    y: center_y + norm_y * segment_length + perp_y * zigzag_width - perp_y * pair_displacement2 * invert_factor,
                    type: 'main',
                    node_type: 'corner'
                }
            ];
            
            return zigzag_points;
        },

        set_balloon_toolbar_visibility() {   
            var visibility = (this.selected_object_is_balloon || this.active_subtool === 'node_editor' || this.balloon_edit_mode_active) 
            && (this.selected_object_array.length == 1 || this.balloon_edit_mode_active);

            return visibility;
        },

        //! Balloon methods
        create_balloon(type = 'speech', options = {}) {
            // Validate panel selection
            if (!this.selected_panel) {
                this.status_message = this.get_t('status.select_panel_first');
                return null;
            }

            const panel = this.selected_panel;

            // Use provided position/dimensions or calculate defaults
            const cx = options.x || (panel.points[0] + panel.points[2]) / 2;
            const cy = options.y || (panel.points[1] + panel.points[5]) / 2;
            const width = options.width || Math.min(panel.points[2] - panel.points[0], 250) * 0.6;
            const height = options.height || Math.min(panel.points[5] - panel.points[1], 120) * 0.6;

            // Centralized: generate points using the universal function
            let points = this.generate_balloon_points(type, width, height);
            let path_data = "";
            const initial_stroke_width = options.strokeWidth || this.balloon_default_stroke_width;

            // Border dash pattern
            const stroke_dash = typeof options.strokeDash !== 'undefined' ? options.strokeDash : (this.balloon_default_stroke_dash || '');

            // Initialize control points and generate universal path
            const enriched_points = this.calculate_initial_control_points(points);
            path_data = this.generate_balloon_path_from_points(enriched_points);
            points = enriched_points;

            // Create balloon group - RAW OBJECT
            const balloon_group_raw = new Konva.Group({
                x: cx, // Position the group at the center point
                y: cy,
                draggable: false, // Default to not draggable, will be managed by tool selection
                name: 'panel_balloon',
                id: 'balloon_' + Date.now(),
            });

            // Mark the Konva object as raw BEFORE any potential reactive assignment
            const balloon_group = markRaw(balloon_group_raw);

            // --- Layer name logic unified with images ---
            let max_layer_number = 0;
            const siblings = panel.content_group.find('.panel_image, .panel_balloon');
            siblings.forEach(layer => {
                const layer_name = layer.getAttr('layer_name');
                if (layer_name && layer_name.startsWith('Layer ')) {
                    const num = parseInt(layer_name.replace('Layer ', ''), 10);
                    if (!isNaN(num) && num > max_layer_number) max_layer_number = num;
                }
            });
            const layer_name = `Layer ${max_layer_number + 1}`;
            balloon_group.setAttr('layer_name', layer_name);

            // Apply opacity to fill color if balloon_fill_alpha is not 1
            let fill_color = options.fill || this.balloon_default_fill;
            if (this.preferences.balloon_defaults.alpha !== 1) {
                // Convert hex or named color to rgba
                if (fill_color.startsWith('#')) {
                    // Convert hex to RGB
                    const r = parseInt(fill_color.slice(1, 3), 16);
                    const g = parseInt(fill_color.slice(3, 5), 16);
                    const b = parseInt(fill_color.slice(5, 7), 16);
                    fill_color = `rgba(${r}, ${g}, ${b}, ${this.preferences.balloon_defaults.alpha})`;
                } else if (!fill_color.startsWith('rgb')) {
                    // For named colors, just use white with opacity as fallback
                    fill_color = `rgba(255, 255, 255, ${this.preferences.balloon_defaults.alpha})`;
                }
            }

            // Balloon shape - positioned at 0,0 relative to the group
            const balloon_shape = new Konva.Path({
                data: path_data,
                fill: fill_color,
                stroke: options.stroke || this.balloon_default_stroke,
                strokeWidth: initial_stroke_width,
                strokeScaleEnabled: true,
                name: 'balloon_shape',
                x: 0,
                y: 0,
                dash: stroke_dash && stroke_dash.length > 0 ? stroke_dash.split(',').map(Number) : []
            });

            const default_margin = 10; // Define default margin/padding

            // Text content
            const text_content = options.text || this.get_t('default_balloon_text');
            
            const balloon_text = new Konva.Text({
                text: text_content,
                fontSize: options.font_size || this.preferences.text_defaults.font_size,
                fontFamily: options.font_family || this.preferences.text_defaults.font_family,
                fill: options.text_color || this.preferences.text_defaults.text_color,
                stroke: options.text_stroke || this.preferences.text_defaults.text_stroke,
                strokeWidth: options.text_stroke_width !== undefined ? options.text_stroke_width : this.preferences.text_defaults.text_stroke_width,
                lineHeight: options.line_height !== undefined ? options.line_height : this.preferences.text_defaults.line_height,
                align: options.align || this.preferences.text_defaults.align,
                fillAfterStrokeEnabled: true,
                padding: options.margin_top !== undefined ? parseInt(options.margin_top) : default_margin,
                x: 0,
                y: 0,
                name: 'balloon_text',
                wrap: 'word',
                ellipsis: false,
                listening: false
            });

            // Add elements to group (the raw group)
            balloon_group.add(balloon_shape);
            balloon_group.add(balloon_text);
            // Save attributes and references on the raw group
            balloon_group.setAttr('balloon_points', points); // Store relative points
            balloon_group.setAttr('balloon_type', type);
            balloon_group.setAttr('object_type', 'balloon');
            balloon_group.setAttr('original_stroke_width', initial_stroke_width);
            balloon_group.setAttr('original_stroke_dash', stroke_dash);
            balloon_group.setAttr('balloon_width', width);
            balloon_group.setAttr('balloon_height', height);
            
            // Set default blend mode and opacity for proper layer controls
            balloon_group.globalCompositeOperation('normal');
            balloon_group.opacity(1);
            balloon_group.setAttr('balloon_size', { x: 1, y: 1 });
            balloon_group.setAttr('previous_balloon_size', { x: 1, y: 1 });
            balloon_group.setAttr('fill_opacity', this.preferences.balloon_defaults.alpha); 
            balloon_group.setAttr('balloon_cx', 0); // Center X relative to group
            balloon_group.setAttr('balloon_cy', 0); // Center Y relative to group

            // Add a flag to track if text has been custom positioned
            balloon_group.setAttr('custom_text_position', false);

            // Direct references for easy access
            balloon_group.shape = balloon_shape;
            balloon_group.text = balloon_text;


            // Add to panel's content group (the raw group)
            panel.content_group.add(balloon_group);

            // Now, recenter the text AFTER the group is added to its parent
            // Use nextTick to ensure dimensions are calculated correctly after potential DOM updates


            // Add event listener to handle transformations
            balloon_group.on('transform', (e) => {
                // Get the group (balloon) that was transformed
                const group = e.target;
                // Recalculate text based on the new transform (stroke is handled by strokeScaleEnabled: false)
                this.recenter_balloon_text(group);
            });


            // Update display
            this.recenter_balloon_text(balloon_group);
            balloon_group.getLayer()?.batchDraw();

            this.panels_layer.batchDraw();
            
            if (!options.skip_history_record) {
                this.push_to_history('add_balloon');
            }

            return balloon_group; // Return the raw group
        },

        generate_balloon_points(type, width, height) {
            const KAPPA = (Math.sqrt(2) - 1) * 4 / 3; // Constant for approximating circles/ellipses with Bezier curves
            const hw = width / 2;  // half width
            const hh = height / 2; // half height
            const k_hw = KAPPA * hw; // kappa * half width
            const k_hh = KAPPA * hh; // kappa * half height

            switch (type) {
                case 'speech':
                    // Oval shape with 4 main points and pre-calculated control points for smooth curves
                    return [
                        { x: 0,    y: -hh,  type: 'main', node_type: 'smooth', cp_out_x: k_hw, cp_out_y: -hh,  cp_in_x: -k_hw, cp_in_y: -hh,  tangency_broken: false },
                        { x: hw,   y: 0,    type: 'main', node_type: 'smooth', cp_out_x: hw,   cp_out_y: k_hh,  cp_in_x: hw,    cp_in_y: -k_hh, tangency_broken: false },
                        { x: 0,    y: hh,   type: 'main', node_type: 'smooth', cp_out_x: -k_hw,cp_out_y: hh,   cp_in_x: k_hw,  cp_in_y: hh,   tangency_broken: false },
                        { x: -hw,  y: 0,    type: 'main', node_type: 'smooth', cp_out_x: -hw,  cp_out_y: -k_hh, cp_in_x: -hw,   cp_in_y: k_hh,  tangency_broken: false }
                    ];

                case 'rectangular':
                    return [
                        {x: -width/2, y: -height/2, type: 'main', node_type: 'corner'},
                        {x: width/2, y: -height/2, type: 'main', node_type: 'corner'},
                        {x: width/2, y: height/2, type: 'main', node_type: 'corner'},
                        {x: -width/2, y: height/2, type: 'main', node_type: 'corner'}
                    ];
                case 'thought':
                    // Cloud-like shape with bubble-style scalloped edges typical of thought balloons
                    return this.generate_adaptive_thought_balloon_points(width, height);

                case 'shout': {
                    // Explosive/jagged shape typical of shout balloons with sharp spikes
                    const shout_points = [];
                    const num_spikes = 12; // Number of spikes around the perimeter
                    
                    for (let i = 0; i < num_spikes; i++) {
                        const angle = (i / num_spikes) * Math.PI * 2;
                        const next_angle = ((i + 1) / num_spikes) * Math.PI * 2;
                        
                        // Create sharp spikes with varying heights for dynamic effect
                        const spike_variation = 0.3 + (Math.sin(i * 1.7) * 0.2); // Varying spike heights
                        
                        // Sharp spike point extending outward
                        const spike_extension = 1.2 + spike_variation;
                        const spike_x = Math.cos(angle) * hw * spike_extension;
                        const spike_y = Math.sin(angle) * hh * spike_extension;
                        
                        // Base point between spikes (closer to center)
                        const mid_angle = angle + (next_angle - angle) / 2;
                        const base_radius = 0.7; // Base points are closer to center
                        const base_x = Math.cos(mid_angle) * hw * base_radius;
                        const base_y = Math.sin(mid_angle) * hh * base_radius;
                        
                        // Add spike point with corner node for sharp edges
                        shout_points.push({
                            x: spike_x,
                            y: spike_y,
                            type: 'main',
                            node_type: 'corner'
                        });
                        
                        // Add base point with corner node to maintain sharp transitions
                        shout_points.push({
                            x: base_x,
                            y: base_y,
                            type: 'main',
                            node_type: 'corner'
                        });
                    }
                    return shout_points;
                }
                default:
                    return [
                        {x: -width/2, y: -height/2, type: 'main', node_type: 'corner'},
                        {x: width/2, y: -height/2, type: 'main', node_type: 'corner'},
                        {x: width/2, y: height/2, type: 'main', node_type: 'corner'},
                        {x: -width/2, y: height/2, type: 'main', node_type: 'corner'}
                    ];
            }
        },

        //! Balloon edit methods
        create_balloon_edit_handles(balloon) {
            // Get control points - already stored as relative to balloon's center
            const rel_points = balloon.getAttr('balloon_points');
            if (!rel_points || !rel_points.length) return;
            
            // Create edit group
            const edit_group = new Konva.Group({
                name: 'balloon_edit_handles',
                x: 0, // The group is positioned at 0,0 relative to the balloon group
                y: 0
            });
            
            // Create handles for each point - position them using the balloon's relative coordinates
            rel_points.forEach((rel_point, index) => {
                // Only create handles for 'main' points initially
                if (rel_point.type !== 'main') return;

                // Use the stored relative coordinates for positioning
                const handle = new Konva.Circle({
                    x: rel_point.x,  // Use the relative coordinates directly
                    y: rel_point.y,
                    radius: 6 / (this.stage ? this.stage.scaleX() : 1), // Adjust radius based on zoom
                    fill: this.get_anchor_handle_color(rel_point),
                    stroke: '#FFFFFF',
                    strokeWidth: 1 / (this.stage ? this.stage.scaleX() : 1), // Adjust stroke based on zoom
                    draggable: true,
                    name: 'balloon_handle',
                    point_index: index,
                    // point_type: rel_point.type, // Not needed if only showing 'main' handles
                    node_type: rel_point.node_type || 'smooth' // Default to smooth if not specified
                });

                // --- Drag Anchor Handle ---
                handle.on('dragmove', e => {
                    const current_index = handle.getAttr('point_index');
                    const points_array = balloon.getAttr('balloon_points');
                    const moved_point = points_array[current_index];

                    // Store old position for delta calculation
                    const old_x = moved_point.x;
                    const old_y = moved_point.y;

                    // Update anchor point position from handle
                    moved_point.x = handle.x();
                    moved_point.y = handle.y();

                    // Calculate delta move
                    const dx = moved_point.x - old_x;
                    const dy = moved_point.y - old_y;

                    // Move control points along with the anchor
                    if (moved_point.cp_in_x !== undefined) { // Check if CPs exist
                        moved_point.cp_in_x += dx;
                        moved_point.cp_in_y += dy;
                        moved_point.cp_out_x += dx;
                        moved_point.cp_out_y += dy;
                    }

                    // Update path data (this can be performance intensive)
                    const new_path = this.generate_balloon_path_from_points(points_array);
                    balloon.shape.data(new_path);

                    // Recenter text during anchor drag
                    this.recenter_balloon_text(balloon);

                    // Update visible CP handles if this node is selected
                    if (this.selected_node_index === current_index && balloon.control_point_handles) {
                        const cp_group = balloon.control_point_handles;
                        const cp_in_circle = cp_group.findOne('.cp_in_handle');
                        const cp_out_circle = cp_group.findOne('.cp_out_handle');
                        const cp_in_line = cp_group.findOne('.cp_in_line');
                        const cp_out_line = cp_group.findOne('.cp_out_line');

                        // Update positions efficiently
                        if (cp_in_circle) cp_in_circle.position({ x: moved_point.cp_in_x, y: moved_point.cp_in_y });
                        if (cp_out_circle) cp_out_circle.position({ x: moved_point.cp_out_x, y: moved_point.cp_out_y });
                        if (cp_in_line) cp_in_line.points([moved_point.x, moved_point.y, moved_point.cp_in_x, moved_point.cp_in_y]);
                        if (cp_out_line) cp_out_line.points([moved_point.x, moved_point.y, moved_point.cp_out_x, moved_point.cp_out_y]);
                    }

                    // Update attributes for saving - Do this once per drag event
                    // balloon.setAttr('balloon_points', points_array); // Already updated by reference

                    // Flag for batched history
                    this.history_pending_balloon_edit = true;

                    // Request redraw once at the end
                    // this.redraw(); // Removed - redraw should happen at stage level if needed, Konva handles shape data change
                    balloon.getLayer()?.batchDraw(); // More targeted redraw
                });

                handle.on('dragend', () => {
                    // Use debounced history push
                    this.debounced_push_history('edit_balloon_shape');
                    // Ensure text is centered in final state
                    this.recenter_balloon_text(balloon);
                    balloon.getLayer()?.batchDraw(); // Ensure final state is drawn
                });

                // --- Select Anchor Handle (Click) ---
                handle.on('click', e => {
                    const current_index = handle.getAttr('point_index');

                    // Ctrl+Click to delete node
                    if (e.evt.ctrlKey) {
                        e.evt.preventDefault(); // Prevent default browser context menu
                        e.evt.stopImmediatePropagation(); // Stop further listeners including potential drag starts

                        let pts = balloon.getAttr('balloon_points');
                        const main_pts_before_delete = pts.filter(p => p.type === 'main');

                        // Check minimum points BEFORE trying to find index
                        if (!main_pts_before_delete || main_pts_before_delete.length <= 3) {
                            console.warn("Cannot delete node, minimum 3 main points required.");
                            // Show feedback popup using the method from ui.js mixin
                            this.show_popup_with_content(
                                this.get_t('balloon.cannot_delete_node'),
                                `<div class="text-center space-y-4">
                                    <p class="text-lg font-medium text-gray-100">${this.get_t('balloon.minimum_points_required')}</p>
                                </div>
                                <div class="flex justify-center mt-8">
                                    <button id="ok_btn" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                                        ${this.get_t('actions.ok')}
                                    </button>
                                </div>`,
                                { type: 'warning' }
                            );
                            // Add listener for the OK button in the popup
                            this.$nextTick(() => {
                                const ok_btn = document.getElementById('ok_btn');
                                if (ok_btn) {
                                    ok_btn.onclick = () => this.close_popup();
                                }
                            });
                            return; // Stop deletion process
                        }

                        // Find the actual index in the original array
                        const actual_index = pts.findIndex(p => p === rel_points[current_index]);
                        if (actual_index === -1) {
                            console.error("Could not find point to delete.");
                            return;
                        }

                        // --- Deletion Logic ---
                        pts.splice(actual_index, 1); // Remove the node

                        // --- Adjust Neighboring Control Points ---
                        const num_points_after_delete = pts.length;
                        if (num_points_after_delete >= 2) {
                            const prev_index = (actual_index - 1 + num_points_after_delete) % num_points_after_delete;
                            const next_index = actual_index % num_points_after_delete;
                            // Adjust CPs respecting tangency
                            this._adjust_neighbor_cps_after_delete(pts, prev_index, next_index);
                        } else if (num_points_after_delete === 1) {
                            // Reset CPs if only one point remains
                            const p = pts[0];
                            p.cp_in_x = p.x; p.cp_in_y = p.y;
                            p.cp_out_x = p.x; p.cp_out_y = p.y;
                        }
                        // --- End Deletion Logic ---

                        balloon.setAttr('balloon_points', pts);
                        balloon.shape.data(this.generate_balloon_path_from_points(pts));

                        this.selected_node_index = null;
                        this.remove_balloon_edit_handles(balloon);
                        this.create_balloon_edit_handles(balloon);
                        this.recenter_balloon_text(balloon);
                        this.push_to_history('delete_balloon_node');
                        balloon.getLayer()?.batchDraw();
                        return; // Explicitly return after handling deletion
                    }

                    // Normal Click: Select node and show CP handles
                    if (this.selected_node_index !== current_index) {
                        // Deselect previous node visually (optional: change color back)
                        const previous_handle = edit_group.findOne(node => node.getAttr('point_index') === this.selected_node_index);
                        if (previous_handle) {
                            // Reset style if needed, e.g., previous_handle.strokeWidth(1);
                        }

                        // Select new node
                        this.selected_node_index = current_index;
                        // Highlight selected node (optional: thicker stroke)
                        // handle.strokeWidth(2); handle.stroke('yellow');

                        // Remove old CP handles and create new ones
                        if (balloon.control_point_handles) {
                            balloon.control_point_handles.destroy();
                            balloon.control_point_handles = null;
                        }
                        // Only create CP handles if the node is smooth
                        if (rel_points[current_index].node_type === 'smooth') {
                            this.create_control_point_handles(balloon, current_index);
                        }
                        this.redraw();
                    }
                });

                // --- Context Menu (Right Click) ---
                handle.on('contextmenu', (e) => {
                    e.evt.preventDefault();
                    const current_index = handle.getAttr('point_index');
                    const points_array = balloon.getAttr('balloon_points');
                    const point_data = points_array[current_index];
                    let needs_path_redraw = false;
                    let previous_node_type = point_data.node_type;


                    if (point_data.node_type === 'smooth') {
                        if (!point_data.tangency_broken) {
                            // Smooth -> Broken Tangency (Blue -> Orange)
                            point_data.tangency_broken = true;
                        } else {
                            // Broken Tangency -> Corner (Orange -> Red)
                            point_data.node_type = 'corner';
                            point_data.tangency_broken = false; // Reset for corner
                            point_data.cp_in_x = point_data.x;
                            point_data.cp_in_y = point_data.y;
                            point_data.cp_out_x = point_data.x;
                            point_data.cp_out_y = point_data.y;
                            needs_path_redraw = true;
                        }
                    } else { // node_type === 'corner'
                        // Corner -> Smooth (Red -> Blue)
                        point_data.node_type = 'smooth';
                        point_data.tangency_broken = false;
                        // Recalculate CPs for this node now that it's smooth
                        // This might involve looking at neighbors, so a broader recalculation or a specific function is needed.
                        // For now, let's assume _recalculate_single_node_cps handles this.
                        this._recalculate_single_node_cps(points_array, current_index);
                        needs_path_redraw = true;
                    }

                    handle.fill(this.get_anchor_handle_color(point_data));

                    // Update or create/destroy CP handles if this node is the selected one
                    if (this.selected_node_index === current_index) {
                        if (balloon.control_point_handles) {
                            balloon.control_point_handles.destroy();
                            balloon.control_point_handles = null;
                        }
                        if (point_data.node_type === 'smooth') {
                             // Create new CP handles for the smooth node
                            this.create_control_point_handles(balloon, current_index);
                        }
                    }

                    if (needs_path_redraw || previous_node_type !== point_data.node_type) {
                        const new_path = this.generate_balloon_path_from_points(points_array);
                        balloon.shape.data(new_path);
                    }

                    balloon.getLayer()?.batchDraw();
                    this.push_to_history('edit_balloon_node_type_via_context');
                });

                // Add handle to edit group
                edit_group.add(handle);
            });

            // Remove the contextmenu listener from the group, apply it to handles individually
            // edit_group.on('contextmenu', ...); // REMOVE THIS

            // Add edit group to the balloon
            balloon.add(edit_group);
            balloon.edit_handles = edit_group; // Store reference

            // If a node was previously selected, recreate its CP handles if it's smooth
            if (this.selected_node_index !== null && rel_points[this.selected_node_index] && rel_points[this.selected_node_index].node_type === 'smooth') {
                 this.create_control_point_handles(balloon, this.selected_node_index);
            }
            // this.redraw(); // Use batchDraw below
            balloon.getLayer()?.batchDraw();
        },

        toggle_balloon_edit_mode(balloon) {
            if (!balloon || balloon.getAttr('object_type') !== 'balloon') {
                console.warn("Attempted to toggle edit mode on non-balloon object:", balloon);
                return;
            }

            const currently_editing_this = balloon.getAttr('editing_mode') === true;

            // --- Exit Edit Mode ---
            if (currently_editing_this) {
                this.exit_balloon_edit_mode(balloon); // Pass the specific balloon
            // --- Enter Edit Mode ---
            } else {
                // First, ensure any other balloon is taken out of edit mode
                if (this.editing_balloon && this.editing_balloon !== balloon) {
                    //console.log("Switching edit mode from balloon:", this.editing_balloon.id(), "to", balloon.id());
                    this.exit_balloon_edit_mode(this.editing_balloon);
                } else if (!this.editing_balloon) {
                    // If no balloon was being edited, ensure the global flag is false initially
                    this.balloon_edit_mode_active = false;
                }

                // Now, enter edit mode for the target balloon
                this.hide_transformer(); // Hide transformer before showing handles
                // Do not change tool here, stay in select tool
                // this.change_tool('select');
                this.change_subtool('node_editor', false); // Activate node editor subtool for UI changes
                
                // IMPORTANTE: Activar el bloqueo de UI para edición de nodos
                this.lock_ui(true, 'balloon_edit_mode');

                balloon.setAttr('editing_mode', true);
                // --- Make balloon non-interactive during edit mode ---
                balloon.draggable(false); // Make non-draggable
                // ---
                this.create_balloon_edit_handles(balloon); // Create the anchor handles

                // Setup listener for adding nodes (Ctrl+Mousedown on shape)
                balloon.shape.off('mousedown.add_node').on('mousedown.add_node', e => {
                    if (!e.evt.ctrlKey) return; // Only proceed if Ctrl key is pressed
                    e.evt.preventDefault();
                    e.evt.stopPropagation();
                    if (this._is_adding_node) return;
                    this._is_adding_node = true;

                    const pointer = this.stage.getPointerPosition();
                    if (!pointer) {
                        this._is_adding_node = false;
                        return;
                    }

                    const transform = balloon.getAbsoluteTransform().copy();
                    transform.invert();
                    const relative_pos = transform.point(pointer);

                    // Use auxiliary function to add point
                    this.add_point_to_balloon(balloon, relative_pos);

                    this.recenter_balloon_text(balloon);
                    this.push_to_history('add_balloon_node');
                    this._is_adding_node = false;
                });

                // Update global state
                this.editing_balloon = balloon; // Store reference to the currently edited balloon
                this.balloon_edit_mode_active = true; // Set global flag

                // Actualizar posición de la barra de herramientas flotante
                this.$nextTick(() => {
                    this.update_balloon_toolbar_position(balloon);
                });

                //console.log("Entered edit mode for balloon:", balloon.id());
            }
            // this.redraw(); // Use batchDraw below
            balloon.getLayer()?.batchDraw();
        },

        remove_balloon_edit_handles(balloon) {
            if (!balloon) return;
            // Also remove CP handles if they exist
            if (balloon.control_point_handles) {
                balloon.control_point_handles.destroy();
                balloon.control_point_handles = null;
            }
            if (balloon.edit_handles) {
                balloon.edit_handles.destroy();
                balloon.edit_handles = null;
            }
            // Reset selected node index when removing handles
            this.selected_node_index = null;
        },

        exit_balloon_edit_mode(balloon = this.editing_balloon) {
            this.selected_object_ref = null;
            // Check if we are in edit mode and have a valid balloon to exit from
            if (!this.balloon_edit_mode_active || !balloon || balloon.getAttr('object_type') !== 'balloon') {
                return; 
            }

            // --- Perform Exit Actions ---
            balloon.setAttr('editing_mode', false);
            // Remove CP handles specifically
            if (balloon.control_point_handles) {
                balloon.control_point_handles.destroy();
                balloon.control_point_handles = null;
            }
            this.remove_balloon_edit_handles(balloon); // Remove anchor handles
            balloon.shape.off('.add_node'); // Remove specific listeners by namespace
            // Only make draggable if select tool is active and not locked
            const should_be_draggable = this.active_tool === 'select' && !balloon.getAttr('locked');
            balloon.draggable(should_be_draggable);
            // ---

            // --- Update Global State ---
            // Only reset global state if the balloon being exited is the currently tracked one
            if (this.editing_balloon === balloon) {
                this.editing_balloon = null;
                this.balloon_edit_mode_active = false;
                this.selected_node_index = null; // Reset selected node index
                this.deselect_subtool(); // Clear the node_editor subtool
                
                // IMPORTANTE: Desbloquear la UI
                this.lock_ui(false);
            }

            // --- Restore UI ---
            // Reselect the object to potentially show the transformer
            // Check if the object still exists before selecting
            if (balloon.getStage()) {
                 // Check if the balloon is still selected or should be re-selected
                 // If another object was selected while editing, don't force re-selection here.
                 // However, typically exiting edit mode implies the user wants to interact with the balloon normally.
                 if (this.selected_object === balloon || !this.selected_object) {
                    this.select_object(balloon);
                 }
            } else {
                console.warn("Balloon to exit edit mode from is no longer on the stage.");
                // If the balloon was deleted while editing, ensure global state is clean
                if (this.editing_balloon === balloon) {
                    this.editing_balloon = null;
                    this.balloon_edit_mode_active = false;
                    this.selected_node_index = null; // Also reset index here
                    this.deselect_subtool();
                    
                    // IMPORTANTE: Asegurar que la UI quede desbloqueada incluso si el globo fue eliminado
                    this.lock_ui(false);
                }
            }

            this.update_balloon_toolbar_position(balloon);

            // Ensure the stage is redrawn
            this.redraw();
        },

        edit_balloon_text(balloon) {
            if (!balloon) return;
            
            // Check if this is a merged balloon with multiple texts
            if (balloon.texts && Array.isArray(balloon.texts) && balloon.texts.length > 0) {
                // For merged balloons, select the first text by default or use the index
                const index = this.active_text_index_in_merged_balloon !== null ? this.active_text_index_in_merged_balloon : 0;
                this.set_active_text_for_editing(balloon, index);
                return;
            }
            
            // Standard single-text balloon
            if (!balloon.findOne) return; // Check if balloon is a group
            const text_node = balloon.findOne('Text');
            if (!text_node) return;

            const default_text_color = this.preferences.text_defaults.text_color;
            const default_text_stroke = this.preferences.text_defaults.text_stroke;
            const default_margin = 10;

            // Reset any active text index for merged balloons
            this.active_text_index_in_merged_balloon = null;

            this.text_editing.active = true;
            this.text_editing.adjust_mode = false;
            this.text_editing.text = text_node.text();
            this.text_editing.font_size = text_node.fontSize();
            this.text_editing.font_family = text_node.fontFamily();
            this.text_editing.align = text_node.align();
            this.text_editing.text_color = text_node.fill() || default_text_color;
            this.text_editing.text_stroke = text_node.stroke() || default_text_stroke;
            this.text_editing.text_stroke_width = text_node.strokeWidth();
            this.text_editing.line_height = text_node.lineHeight() || 1;

            const style = (text_node.fontStyle && text_node.fontStyle()) || '';
            this.text_editing.is_bold = style.includes('bold');
            this.text_editing.is_italic = style.includes('italic');

            this.change_subtool('text', false);
            this.redraw(); // Or use a more targeted reactivity update if possible
        },

        apply_balloon_text_changes(balloon) {
            // This function applies ALL changes from text_editing upon confirmation
            if (!balloon) return;
            
            let target_text_node;
            
            // Handle multiple texts (merged balloons)
            if (balloon.texts && Array.isArray(balloon.texts)) {
                const index = this.active_text_index_in_merged_balloon;
                if (index !== null && index !== undefined && balloon.texts[index]) {
                    target_text_node = balloon.texts[index];
                } else {
                    return;
                }
            } else if (balloon.findOne) {
                // Handle single text (standard balloon)
                target_text_node = balloon.findOne('Text');
                if (!target_text_node) return;
            } else {
                return;
            }

            const default_text_color = this.preferences.text_defaults.text_color;
            const default_text_stroke = this.preferences.text_defaults.text_stroke;

            // Apply styles individually to the target node
            target_text_node.text(this.text_editing.text);
            target_text_node.fontFamily(this.text_editing.font_family);
            const font_size = parseInt(this.text_editing.font_size);
            if (!isNaN(font_size) && font_size > 0) target_text_node.fontSize(font_size);
            target_text_node.align(this.text_editing.align);
            target_text_node.fill(this.text_editing.text_color && this.text_editing.text_color.startsWith('#') ? this.text_editing.text_color : default_text_color);
            target_text_node.stroke(this.text_editing.text_stroke && this.text_editing.text_stroke.startsWith('#') ? this.text_editing.text_stroke : default_text_stroke);
            const stroke_width = parseFloat(this.text_editing.text_stroke_width);
            target_text_node.strokeWidth(stroke_width !== null && stroke_width !== undefined && !isNaN(stroke_width) ? stroke_width : 0);
            const line_height = parseFloat(this.text_editing.line_height);
            target_text_node.lineHeight(line_height !== null && line_height !== undefined && !isNaN(line_height) ? line_height : 1);
            target_text_node.fillAfterStrokeEnabled(true);
            target_text_node.padding(10);

            this.recenter_balloon_text(balloon); // Recenter after all changes

            this.redraw(); // Use global redraw consistently

            // Use the specific balloon reference for history
            this.push_to_history('edit_balloon_text');
            this.text_editing.active = false;
            this.active_subtool = null;
            this.active_text_index_in_merged_balloon = null; // Reset active text index
        },
        
        cancel_balloon_text_editing() {
            // Desactivar modo edición de texto sin aplicar cambios
            if (this.text_editing.adjust_mode) {
                this.exit_text_adjust_mode();
            }
            
            this.text_editing.active = false;
            this.active_subtool = null;
            this.active_text_index_in_merged_balloon = null; // Reset active text index in merged balloons
        },
        
        /**
         * Sets the active text node in a merged balloon for editing
         * @param {Object} balloon - The balloon group containing multiple texts
         * @param {Number} index - Index of the text to edit within balloon.texts array
         */
        set_active_text_for_editing(balloon, index) {
            if (!balloon || !balloon.texts || !Array.isArray(balloon.texts) || index === undefined) return;
            
            const text_node = balloon.texts[index];
            if (!text_node) return;
            
            // Store the index of the currently active text node for UI highlighting
            this.active_text_index_in_merged_balloon = index;
            this.current_editing_text_node_index = index;
            
            // Fill text_editing properties with the selected text node's values
            const default_text_color = this.preferences.text_defaults.text_color;
            const default_text_stroke = this.preferences.text_defaults.text_stroke;

            this.text_editing.active = true;
            this.text_editing.adjust_mode = false;
            this.text_editing.text = text_node.text();
            this.text_editing.font_size = text_node.fontSize();
            this.text_editing.font_family = text_node.fontFamily();
            this.text_editing.align = text_node.align();
            this.text_editing.text_color = text_node.fill() || default_text_color;
            this.text_editing.text_stroke = text_node.stroke() || default_text_stroke;
            this.text_editing.text_stroke_width = text_node.strokeWidth();
            this.text_editing.line_height = text_node.lineHeight() || 1;
            
            const style = (text_node.fontStyle && text_node.fontStyle()) || '';
            this.text_editing.is_bold = style.includes('bold');
            this.text_editing.is_italic = style.includes('italic');
            this.change_subtool('text', false);
            this.redraw();
        },
        
        recenter_balloon_text(balloon, preserve_text_rotation = true) { 
            if (!balloon || !balloon.shape) return;

            const group = balloon;
            const group_scale_x = group.scaleX() || 1;
            const group_scale_y = group.scaleY() || 1;
            if (group_scale_x === 0 || group_scale_y === 0) return;

            const inverse_scale_x = 1 / group_scale_x;
            const inverse_scale_y = 1 / group_scale_y;

            const text_nodes_to_process = [];
            if (balloon.texts && Array.isArray(balloon.texts)) {
                balloon.texts.forEach(tn => {
                    if (tn) text_nodes_to_process.push(tn);
                });
            } else if (balloon.text) {
                text_nodes_to_process.push(balloon.text);
            } else {
                return; 
            }
            const shape_node = balloon.shape;
            const balloon_type = balloon.getAttr('balloon_type');
            
            // Adjust padding allowance based on balloon type
            let padding_allowance = 0.80;
            if (balloon_type === 'shout') {
                padding_allowance = 0.45; // Make text area narrower for shout balloons
            }

            text_nodes_to_process.forEach(text_node => {
                text_node.scaleX(inverse_scale_x);
                text_node.scaleY(inverse_scale_y);

                if (!preserve_text_rotation) {
                    text_node.rotation(-group.rotation());
                }
                
                let box_for_text;
                const custom_box_on_node = text_node.getAttr('custom_box_for_recenter');

                if (custom_box_on_node && typeof custom_box_on_node.width === 'number' && custom_box_on_node.width > 0) {
                    box_for_text = { ...custom_box_on_node }; // Use a copy
                } else if (group.getAttr('custom_text_position') === true &&
                           typeof group.getAttr('custom_text_box_width') === 'number' && group.getAttr('custom_text_box_width') > 0) {
                    box_for_text = {
                        x: group.getAttr('custom_text_box_x'),
                        y: group.getAttr('custom_text_box_y'),
                        width: group.getAttr('custom_text_box_width'),
                        height: group.getAttr('custom_text_box_height')
                    };
                } else {
                    box_for_text = shape_node.getClientRect({ relativeTo: group });
                    
                    // Additional adjustments for shout balloons
                    if (balloon_type === 'shout') {
                        // Make the text box slightly smaller in height from the top
                        const height_reduction = box_for_text.height * 0.1; // Reduce height by 10%
                        box_for_text.y += height_reduction * 0.7; // Move down slightly (70% of the reduction from top)
                        box_for_text.height -= height_reduction; // Reduce total height
                    }
                }
                const target_visual_width = box_for_text.width * padding_allowance;
                const required_unscaled_width = target_visual_width / inverse_scale_x;

                text_node.width(required_unscaled_width);
                const text_height = text_node.height(); 

                // Check if text actually fits in available height
                const available_height = box_for_text.height * padding_allowance;
                const scaled_text_height = text_height * inverse_scale_y;

                text_node.visible(true); // Disable ellipsis to allow full text rendering
                
                // If text doesn't fit vertically, reduce width proportionally to make it disappear
                if (scaled_text_height > available_height) {
                    text_node.visible(false); 
                }

                const center_x_of_box = box_for_text.x + box_for_text.width / 2;
                const center_y_of_box = box_for_text.y + box_for_text.height / 2;

                text_node.x(center_x_of_box);
                text_node.y(center_y_of_box);

                text_node.offsetX(required_unscaled_width / 2);
                text_node.offsetY(text_height / 2);
            });

            const layer = group.getLayer();
            if (layer) {
                layer.batchDraw();
            }
        },
          
        change_balloon_type(balloon, type, force = false, push_history = true) {
            if (!balloon || balloon.getAttr('object_type') !== 'balloon') return;

            if (balloon.getAttr('balloon_type') === type && !force) {
                return; // Do nothing if the type is already the requested one
            }
            
            // Check if balloon has multiple texts - prevent conversion for better UX
            const has_multiple_texts = balloon.texts && Array.isArray(balloon.texts) && balloon.texts.length > 1;
            if (has_multiple_texts) {
                console.warn('Cannot change type for balloons with multiple texts');
                return;
            }
            
            // Capture original balloon dimensions and center BEFORE any changes
            var original_balloon_bounds = balloon.getClientRect();
            var original_width = original_balloon_bounds.width;
            var original_height = original_balloon_bounds.height;
            var original_center_x = original_balloon_bounds.x + original_balloon_bounds.width / 2;
            var original_center_y = original_balloon_bounds.y + original_balloon_bounds.height / 2;
            
            // Use transformer dimensions for consistency
            var width = this.transformer.getClientRect().width;
            var height = this.transformer.getClientRect().height;            

            // Update type
            balloon.setAttr('balloon_type', type);
            
            // Centralized: generate points using the universal function
            let points = this.generate_balloon_points(type, width, height);
            const new_enriched = this.calculate_initial_control_points(points);
            balloon.setAttr('balloon_points', new_enriched);
            const new_path_data = this.generate_balloon_path_from_points(new_enriched);
            // Update shape
            balloon.shape.data(new_path_data);
            
            // Apply distortion to make the balloon fit exactly in the original balloon area
            this.distort_balloon_to_fit(balloon, original_width, original_height);
            
            // Ensure balloon center is preserved after type change
            const new_balloon_bounds = balloon.getClientRect();
            const new_center_x = new_balloon_bounds.x + new_balloon_bounds.width / 2;
            const new_center_y = new_balloon_bounds.y + new_balloon_bounds.height / 2;
            
            const offset_x = original_center_x - new_center_x;
            const offset_y = original_center_y - new_center_y;
            
            if (Math.abs(offset_x) > 0.1 || Math.abs(offset_y) > 0.1) {
                balloon.x(balloon.x() + offset_x);
                balloon.y(balloon.y() + offset_y);
            }
            
            // If in edit mode, update handles
            if (balloon.getAttr('editing_mode')) {
                this.remove_balloon_edit_handles(balloon);
                this.create_balloon_edit_handles(balloon);
            }

            this.recenter_balloon_text(balloon); // Recenter text after type change
            this.update_balloon_toolbar_position(balloon); // Update toolbar position
            this.redraw();

            if (push_history) {
                this.push_to_history('change_balloon_type');
            }
        },

        update_balloon_fill(color) {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon') return;

            if (this.selected_object.shape) {
                // Performance optimization: use requestAnimationFrame to avoid lag
                if (this.color_update_timer) {
                    cancelAnimationFrame(this.color_update_timer);
                }

                // Store the new color as the original fill (keep the pure hex/rgb value)
                this.selected_object.setAttr('original_fill', color);
                
                this.color_update_timer = requestAnimationFrame(() => {
                    let applied_color = color;
                    
                    if (this.balloon_fill_alpha < 1) {
                        // Convert hex to RGBA
                        if (color.startsWith('#')) {
                            const r = parseInt(color.slice(1, 3), 16);
                            const g = parseInt(color.slice(3, 5), 16);
                            const b = parseInt(color.slice(5, 7), 16);
                            applied_color = `rgba(${r}, ${g}, ${b}, ${this.balloon_fill_alpha})`;
                        } else if (color.startsWith('rgb(')) {
                            // Convert rgb to rgba
                            const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)/);
                            if (match) {
                                const r = parseInt(match[1], 10);
                                const g = parseInt(match[2], 10);
                                const b = parseInt(match[3], 10);
                                applied_color = `rgba(${r}, ${g}, ${b}, ${this.balloon_fill_alpha})`;
                            }
                        }
                    }
                    
                    // Apply the color to the shape
                    this.selected_object.shape.fill(applied_color);
                    
                    // Update thought bubble styles if they exist
                    this.update_thought_bubble_styles(this.selected_object);
                    
                    this.redraw();
                    
                    this.color_update_timer = null;
                    // History is pushed by @change in template
                });
            }
        },
        update_balloon_stroke(color) {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon') return;
            const balloon = this.selected_object;
            if (balloon.shape) {
                if (this.stroke_update_timer) cancelAnimationFrame(this.stroke_update_timer);
                balloon.setAttr('original_stroke', color); // Store original stroke color if needed
                this.stroke_update_timer = requestAnimationFrame(() => {
                    balloon.shape.stroke(color);
                    
                    // Update thought bubble styles if they exist
                    this.update_thought_bubble_styles(balloon);
                    
                    this.redraw();
                    this.stroke_update_timer = null;
                });
            }
        },
        // Update border dash for the selected balloon
        update_selected_balloon_stroke_dash(value) {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon' || !this.selected_object.shape) return;
            const balloon = this.selected_object;
            // Store as string for serialization
            balloon.setAttr('original_stroke_dash', value);
            // Apply to Konva shape
            balloon.shape.dash(value && value.length > 0 ? value.split(',').map(Number) : []);
            // Update UI variable
            this.selected_object_stroke_dash = value;
            this.redraw();
        },
        // New method to update stroke width for the selected balloon
        update_selected_balloon_stroke_width(value) {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon' || !this.selected_object.shape) return;
            const balloon = this.selected_object; // Use local ref

            let new_width = parseInt(value, 10);

            // Validate the input (allow 0, but block negative values)
            if (isNaN(new_width) || new_width < 0) {
                new_width = 0; // Default to 0 if invalid or negative
            } else if (new_width > 20) {
                new_width = 20; // Max width
            }

            // Apply the new stroke width to the shape
            balloon.shape.strokeWidth(new_width);

            // Update thought bubble styles if they exist
            this.update_thought_bubble_styles(balloon);

            // Update the stored original stroke width attribute as well,
            // so future scaling adjustments (if strokeScaleEnabled were true) would use this new base.
            // Even with strokeScaleEnabled: false, it's good practice to keep this consistent.
            balloon.setAttr('original_stroke_width', new_width);
            this.selected_object_shape_stroke_width = new_width; // Update local ref for UI

            this.redraw();
            // History is pushed by @change in template
        },     
        on_balloon_fill_alpha_input() {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon') {
                return;
            }
            
            if (this.alpha_update_timer) {
                cancelAnimationFrame(this.alpha_update_timer);
            }
            
            this.alpha_update_timer = requestAnimationFrame(() => {
                if (this.selected_object.shape) {
                    let original_fill = this.selected_object.getAttr('original_fill');
                    const current_fill = this.selected_object.shape.fill();
                    
                    if (!original_fill || (current_fill && current_fill.startsWith('#'))) {
                        original_fill = current_fill;
                        this.selected_object.setAttr('original_fill', original_fill);
                    }
                    
                    let r = 255, g = 255, b = 255;
                    
                    if (original_fill) {
                        if (original_fill.startsWith('#')) {
                            r = parseInt(original_fill.slice(1, 3), 16);
                            g = parseInt(original_fill.slice(3, 5), 16);
                            b = parseInt(original_fill.slice(5, 7), 16);
                        } else if (original_fill.startsWith('rgb')) {
                            const match = original_fill.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
                            if (match) {
                                r = parseInt(match[1], 10);
                                g = parseInt(match[2], 10);
                                b = parseInt(match[3], 10);
                            }
                        }
                    }
                    
                    const rgba_color = `rgba(${r}, ${g}, ${b}, ${this.balloon_fill_alpha})`;
                    this.selected_object.shape.fill(rgba_color);
                    this.selected_object.setAttr('fill_opacity', this.balloon_fill_alpha);
                    
                    // Update thought bubble styles if they exist
                    this.update_thought_bubble_styles(this.selected_object);
                    
                    this.redraw();
                }
                
                this.alpha_update_timer = null;
                // History pushed by @change in template
            });
        },

        set_balloon_type(type) {
            this.active_balloon_type = type;
            this.status_message = this.get_t('status.selected_balloon_type', { type });
        },

        get_color_as_hex(color) {
            if (!color) return '#FFFFFF';
            
            // If already hex format, return as is
            if (color.startsWith('#')) return color;
            
            let r = 255, g = 255, b = 255; // Default white
            
            // Extract RGB from rgba format
            if (color.startsWith('rgba')) {
                const match = color.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
                if (match) {
                    r = parseInt(match[1], 10);
                    g = parseInt(match[2], 10);
                    b = parseInt(match[3], 10);
                }
            } 
            // Extract RGB from rgb format
            else if (color.startsWith('rgb(')) {
                const match = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
                if (match) {
                    r = parseInt(match[1], 10);
                    g = parseInt(match[2], 10);
                    b = parseInt(match[3], 10);
                }
            }
            
            // Convert to hex
            return '#' + 
                (r < 16 ? '0' : '') + r.toString(16) + 
                (g < 16 ? '0' : '') + g.toString(16) + 
                (b < 16 ? '0' : '') + b.toString(16);
        },

        get_balloon_fill_color() {
            if (!this.selected_object || !this.selected_object.shape) return '#FFFFFF';
            
            // Check if we have stored the original fill color
            const original_fill = this.selected_object.getAttr('original_fill');
            if (original_fill) return original_fill;
            
            // Otherwise, extract it from the current fill
            const current_fill = this.selected_object.shape.fill();
            
            // Convert to hex and store for future use
            const hex_color = this.get_color_as_hex(current_fill);
            
            // Cache the original color to avoid repeated conversions
            this.selected_object.setAttr('original_fill', hex_color);
            
            return hex_color;
        },

        clear_balloon_edit_modes() {
            // Exit edit mode for any balloons that might be in it
            this.panels.forEach(panel => {
                panel.content_group.find('.panel_balloon').forEach(balloon => {
                    if (balloon.getAttr('editing_mode')) {
                        this.remove_balloon_edit_handles(balloon);
                        balloon.setAttr('editing_mode', false);
                    }
                });
            });
            
            // Reset balloon edit mode flag
            this.balloon_edit_mode_active = false;
        },

        update_balloon_toolbar_position(balloon) {
            if (!balloon || !balloon.shape || !this.stage) return;            
            
            // Cancel any pending animation frame request to avoid unnecessary updates
            if (this.toolbar_update_timer) {
                cancelAnimationFrame(this.toolbar_update_timer);
            }
            
            // Use $nextTick to ensure DOM is updated before measuring
            this.$nextTick(() => {
            // Use requestAnimationFrame for smoother performance
            this.toolbar_update_timer = requestAnimationFrame(() => {
                // Get balloon's bounding box
                const balloon_box = balloon.getClientRect();
                
                // Calculate position BELOW the balloon with more spacing
                const center_x = balloon_box.x + balloon_box.width / 2;
                const bottom_y = balloon_box.y + balloon_box.height + 110; 
                
                // Get container's offset from the page
                const container = this.stage.container();
                const container_rect = container.getBoundingClientRect();
                
                // Calculate position relative to the container
                const adjusted_x = center_x - container_rect.left;
                const adjusted_y = bottom_y - container_rect.top;
                
                // Get toolbar and measure it AFTER Vue has updated the DOM
                const toolbar = document.getElementById('balloon_toolbar');
                
                // If toolbar exists, calculate its width to prevent it from going offscreen
                let toolbar_width = 478; // Default estimate
                if (toolbar) {
                    // Force reflow to ensure we get the correct width after DOM updates
                    toolbar_width = toolbar.offsetWidth || toolbar_width;
                }                
                
                // Adjust x position to keep toolbar on screen
                let final_x = adjusted_x - (toolbar_width / 2); // Center toolbar
                final_x = balloon_box.x + (balloon_box.width / 2) - 31 - (toolbar_width / 2); // Center toolbar below balloon


                //!New adjust X
                var total_inumtable_canvas_width = this.stage.width() - 64; // Adjusted total canvas width to account for the initial difference
                var current_canvas_width = this.stage.container().getBoundingClientRect().width;

                var displacement_x = (total_inumtable_canvas_width - current_canvas_width) / 2;
                final_x = final_x - displacement_x;

                var total_inumtable_canvas_height = this.stage.height() - 43; // Adjusted total canvas height to account for the initial difference
                var current_canvas_height = this.stage.container().getBoundingClientRect().height;

                var displacement_y = (total_inumtable_canvas_height - current_canvas_height) / 2;
                var final_y = adjusted_y - displacement_y;

                // // Get window dimensions to prevent toolbar from going offscreen
                // const window_width = window.innerWidth;
                
                // // Check left edge
                // if (final_x < 10) {
                //     final_x = 10;
                // }
                
                // // Check right edge
                // if (final_x + toolbar_width > window_width - 10) {
                //     final_x = window_width - toolbar_width - 10;
                // }
                
                // Update toolbar position in the data
                this.balloon_toolbar_position = {
                    x: final_x,
                    y: final_y
                };


                



            });
            });
        },

        /**
         * Universal SVG path generator using explicit control points.
         * @param {Array} points - Array of points with cp_in_x/y, cp_out_x/y, node_type.
         * @returns {string} SVG path data.
         */
        generate_balloon_path_from_points(points) {
          if (!points || points.length < 2) return '';
          
          // Only main anchor points define the path
          const main_points = points.filter(p => p.type === 'main');
          if (main_points.length < 2) return '';
          
          let path = '';
          let current_subpath_start = 0;
          
          // Process each subpath separately
          while (current_subpath_start < main_points.length) {
            // Find the end of current subpath
            let current_subpath_end = current_subpath_start + 1;
            while (current_subpath_end < main_points.length && 
                   !main_points[current_subpath_end].hasOwnProperty('subpath_start')) {
              current_subpath_end++;
            }
            
            // Extract current subpath points
            const subpath_points = main_points.slice(current_subpath_start, current_subpath_end);
            
            if (subpath_points.length >= 2) {
              // Start new subpath
              path += `M${subpath_points[0].x},${subpath_points[0].y} `;
              
              // Process each segment in the subpath
              for (let i = 0; i < subpath_points.length; i++) {
                const current = subpath_points[i];
                const next = subpath_points[(i + 1) % subpath_points.length];
                
                if (current.node_type === 'corner' && next.node_type === 'corner') {
                  path += `L${next.x},${next.y} `;
                } else {
                  // Cubic Bezier using explicit handles
                  const cp1x = current.cp_out_x;
                  const cp1y = current.cp_out_y;
                  const cp2x = next.cp_in_x;
                  const cp2y = next.cp_in_y;
                  path += `C${cp1x},${cp1y} ${cp2x},${cp2y} ${next.x},${next.y} `;
                }
              }
              
              // Close current subpath
              path += 'Z ';
            }
            
            // Move to next subpath
            current_subpath_start = current_subpath_end;
          }
          
          return path.trim();
        },

        /**
         * Compute initial control points for each anchor (Bezier handles).
         * Ensures smooth nodes have collinear handles based on neighbors.
         * Universal logic applied to all smooth nodes.
         * For 'corner' nodes, control points are set to the node's own position.
         * @param {Array} points - Array of base anchor points with x, y, node_type.
         * @returns {Array} Points enriched with cp_in_x/y, cp_out_x/y, and tangency_broken flag.
         */
        calculate_initial_control_points(points) {
            const num_points = points.length;
            if (num_points === 0) return [];

            const enriched_points = points.map(p => ({ ...p })); // Create a new array with copies

            for (let i = 0; i < num_points; i++) {
                const current_point = enriched_points[i];

                if (current_point.node_type === 'corner') {
                    current_point.cp_in_x = current_point.x;
                    current_point.cp_in_y = current_point.y;
                    current_point.cp_out_x = current_point.x;
                    current_point.cp_out_y = current_point.y;
                    current_point.tangency_broken = false; // Not applicable but good to have a defined state
                } else { // 'smooth'
                    if (current_point.tangency_broken === undefined) {
                        current_point.tangency_broken = false;
                    }
                    // If CPs are not already defined, calculate them
                    if (current_point.cp_in_x === undefined || current_point.cp_out_x === undefined) {
                        const prev_i = (i - 1 + num_points) % num_points;
                        const next_i = (i + 1) % num_points;
                        const prev_point = enriched_points[prev_i];
                        const next_point = enriched_points[next_i];

                        // Simplified control point calculation (tangent-based)
                        // This is a common method; adjust if you have a more specific one
                        const tangent_x = (next_point.x - prev_point.x) / 2;
                        const tangent_y = (next_point.y - prev_point.y) / 2;

                        // Distance for control points (e.g., 1/3 of segment length, or a fixed value)
                        // This factor can be adjusted for desired smoothness/curve shape
                        const cp_dist_factor = 0.3; 
                        const dist_prev = Math.sqrt(Math.pow(current_point.x - prev_point.x, 2) + Math.pow(current_point.y - prev_point.y, 2));
                        const dist_next = Math.sqrt(Math.pow(next_point.x - current_point.x, 2) + Math.pow(next_point.y - current_point.y, 2));

                        current_point.cp_in_x = current_point.x - tangent_x * cp_dist_factor * (dist_prev / (dist_prev + dist_next) || 1);
                        current_point.cp_in_y = current_point.y - tangent_y * cp_dist_factor * (dist_prev / (dist_prev + dist_next) || 1);
                        current_point.cp_out_x = current_point.x + tangent_x * cp_dist_factor * (dist_next / (dist_prev + dist_next) || 1);
                        current_point.cp_out_y = current_point.y + tangent_y * cp_dist_factor * (dist_next / (dist_prev + dist_next) || 1);
                    }
                }
            }
            return enriched_points;
        },

        /**
         * Create control point handles for a selected anchor node.
         * @param {Konva.Group} balloon - Target balloon group.
         * @param {number} node_index - Index of the anchor in balloon_points.
         */
        create_control_point_handles(balloon, node_index) {
            const points = balloon.getAttr('balloon_points');
            // Check node type BEFORE creating handles
            if (!points || !points[node_index] || points[node_index].node_type === 'corner') {
                if (balloon.control_point_handles) {
                     balloon.control_point_handles.destroy();
                     balloon.control_point_handles = null;
                }
                return;
            }

            if (balloon.control_point_handles) {
                balloon.control_point_handles.destroy();
            }

            const cp_group = new Konva.Group({ name: 'control_point_handles' });
            const node = points[node_index];

            // Determine colors based on tangency
            const use_same_color = !node.tangency_broken;
            const in_color = use_same_color ? '#00ffff' : '#ff00ff'; // Cyan if same, Magenta if broken
            const out_color = '#00ffff'; // Always Cyan for OUT handle

            const create_handle = (cp_key, color, handle_name, line_name) => {
                // ... (create circle and line with given color) ...
                const x_key = `${cp_key}_x`;
                const y_key = `${cp_key}_y`;

                const circle = new Konva.Circle({
                    x: node[x_key],
                    y: node[y_key],
                    radius: 4 / (this.stage ? this.stage.scaleX() : 1), // Adjust radius
                    fill: color,
                    stroke: 'white',
                    strokeWidth: 1 / (this.stage ? this.stage.scaleX() : 1), // Adjust stroke
                    draggable: true,
                    name: handle_name
                });
                const line = new Konva.Line({
                    points: [node.x, node.y, node[x_key], node[y_key]],
                    stroke: color,
                    strokeWidth: 1 / (this.stage ? this.stage.scaleX() : 1), // Adjust stroke
                    name: line_name,
                    listening: false, // Lines shouldn't block events
                });

                // Store original positions on drag start for Alt key logic
                circle.on('dragstart', (e) => {
                    const pts = balloon.getAttr('balloon_points');
                    const current_node = pts[node_index];
                    this._cp_drag_start_positions = {
                        cp_in_x: current_node.cp_in_x,
                        cp_in_y: current_node.cp_in_y,
                        cp_out_x: current_node.cp_out_x,
                        cp_out_y: current_node.cp_out_y,
                    };
                });

                circle.on('dragmove', (e) => {
                    // Use requestAnimationFrame for performance
                    if (this._cp_drag_update_pending) return;
                    this._cp_drag_update_pending = true;

                    // Store event data for processing in the animation frame
                    this._last_cp_drag_event = { e, balloon, node_index, cp_key, circle, line, cp_group };

                    requestAnimationFrame(() => this._process_cp_drag_update());
                });

                circle.on('dragend', () => {
                    this._cp_drag_start_positions = null; // Clear stored positions
                    this.debounced_push_history('edit_balloon_node');
                });

                cp_group.add(line, circle);
            };

            // Create handles with potentially different colors
            create_handle('cp_in', in_color, 'cp_in_handle', 'cp_in_line');
            create_handle('cp_out', out_color, 'cp_out_handle', 'cp_out_line');

            balloon.add(cp_group);
            balloon.control_point_handles = cp_group;
            // this.redraw(); // Use batchDraw below
            balloon.getLayer()?.batchDraw();
        },

        // New method to process CP drag updates within requestAnimationFrame
        _process_cp_drag_update() {
            if (!this._last_cp_drag_event || !this._cp_drag_start_positions) {
                this._cp_drag_update_pending = false;
                return;
            }

            const { e, balloon, node_index, cp_key, circle, line, cp_group } = this._last_cp_drag_event;
            const points = balloon.getAttr('balloon_points');
            // Added check for points existence
            if (!points || !points[node_index]) {
                 this._cp_drag_update_pending = false;
                 return;
            }
            const node = points[node_index];
            const x_key = `${cp_key}_x`;
            const y_key = `${cp_key}_y`;

            // Update the position of the dragged CP in the data array
            node[x_key] = circle.x();
            node[y_key] = circle.y();

            const is_alt_pressed = e.evt.altKey;
            const opposite_cp_key = cp_key === 'cp_in' ? 'cp_out' : 'cp_in';
            const opposite_x_key = `${opposite_cp_key}_x`;
            const opposite_y_key = `${opposite_cp_key}_y`;

            // --- Handle Opposite CP ---
            if (!node.tangency_broken) {
                // Calculate vector from anchor to dragged handle
                const dx_dragged = node[x_key] - node.x;
                const dy_dragged = node[y_key] - node.y;
                const dist_dragged_sq = dx_dragged * dx_dragged + dy_dragged * dy_dragged;

                if (dist_dragged_sq > 1e-9) { // Avoid issues with zero distance
                    const dist_dragged = Math.sqrt(dist_dragged_sq);
                    let dist_opposite; // Distance for the opposite handle

                    // Determine the distance for the opposite handle
                    if (is_alt_pressed) {
                        // ALT pressed: Maintain opposite handle's original distance
                        const orig_opp_x = this._cp_drag_start_positions[opposite_x_key];
                        const orig_opp_y = this._cp_drag_start_positions[opposite_y_key];
                        dist_opposite = Math.hypot(orig_opp_x - node.x, orig_opp_y - node.y);
                    } else {
                        // NO ALT pressed: Mirror the dragged handle's distance (symmetric behavior)
                        dist_opposite = dist_dragged;
                    }

                    // Calculate the angle of the vector from anchor to the dragged handle
                    const angle_dragged = Math.atan2(dy_dragged, dx_dragged);
                    // The opposite handle should be 180 degrees away
                    const angle_opposite = angle_dragged + Math.PI;

                    // Calculate the position of the opposite handle using the angle and its specific distance
                    node[opposite_x_key] = node.x + Math.cos(angle_opposite) * dist_opposite;
                    node[opposite_y_key] = node.y + Math.sin(angle_opposite) * dist_opposite;

                } else { // If dragged handle is (almost) at the anchor, snap opposite to anchor too
                    node[opposite_x_key] = node.x;
                    node[opposite_y_key] = node.y;
                }

                // Update the visual position of the opposite handle circle and line
                const opposite_circle = cp_group.findOne(`.${opposite_cp_key}_handle`);
                const opposite_line = cp_group.findOne(`.${opposite_cp_key}_line`);
                if (opposite_circle) {
                    opposite_circle.position({ x: node[opposite_x_key], y: node[opposite_y_key] });
                }
                if (opposite_line) {
                    opposite_line.points([node.x, node.y, node[opposite_x_key], node[opposite_y_key]]);
                }

            } // else (tangency broken): Opposite handle is not moved automatically

            // Update the line for the currently dragged handle
            line.points([node.x, node.y, node[x_key], node[y_key]]);

            // Regenerate the main balloon path and update shape data
            const new_path = this.generate_balloon_path_from_points(points);
            balloon.shape.data(new_path);

            this.recenter_balloon_text(balloon);
            this.history_pending_balloon_edit = true;
            balloon.getLayer()?.batchDraw();
            this._cp_drag_update_pending = false;
        },

        /**
         * Get the appropriate color for an anchor handle based on node type and tangency.
         * @param {object} point_data - The point data object {node_type, tangency_broken}.
         * @returns {string} Hex color code.
         */
        get_anchor_handle_color(point_data) {
            if (point_data.node_type === 'corner') {
                return '#FF0000'; // Red for corner nodes
            } else if (point_data.node_type === 'smooth' && point_data.tangency_broken) {
                return '#FFA500'; // Orange for broken tangency smooth nodes
            }
            return '#007BFF'; // Blue for smooth nodes (default)
        },

        /**
         * Updates the colors of existing control point handles based on tangency state.
         * @param {Konva.Group} cp_group - The group containing the CP handles.
         * @param {boolean} tangency_broken - The current tangency state.
         */
        update_control_point_handle_colors(cp_group, tangency_broken) {
            if (!cp_group) return;

            const cp_in_circle = cp_group.findOne('.cp_in_handle');
            const cp_out_circle = cp_group.findOne('.cp_out_handle');
            const color = tangency_broken ? '#FFA500' : '#007BFF'; // Orange if broken, Blue if smooth

            if (cp_in_circle) cp_in_circle.fill(color);
            if (cp_out_circle) cp_out_circle.fill(color);
        },

        // Method to explicitly set the node type from UI buttons
        set_selected_node_type(type) {
            if (this.editing_balloon && this.selected_node_index !== null) {
                const balloon = this.editing_balloon;
                const points_array = balloon.getAttr('balloon_points');
                const point_data = points_array[this.selected_node_index];
                const edit_handles_group = balloon.findOne('.balloon_edit_handles');
                const handle = edit_handles_group ? edit_handles_group.findOne(node => node.getAttr('point_index') === this.selected_node_index) : null;

                let needs_path_redraw = false;
                let previous_node_type = point_data.node_type;
                let previous_tangency_broken = point_data.tangency_broken;

                switch (type) {
                    case 'smooth':
                        point_data.node_type = 'smooth';
                        point_data.tangency_broken = false;
                        if (previous_node_type === 'corner') {
                            this._recalculate_single_node_cps(points_array, this.selected_node_index);
                            needs_path_redraw = true;
                        }
                        this.create_control_point_handles(balloon, this.selected_node_index);
                        break;
                    case 'broken_tangency':
                        point_data.node_type = 'smooth';
                        point_data.tangency_broken = true;
                        if (previous_node_type === 'corner') {
                            this._recalculate_single_node_cps(points_array, this.selected_node_index);
                            needs_path_redraw = true;
                        }
                        this.create_control_point_handles(balloon, this.selected_node_index);
                        break;
                    case 'corner':
                        point_data.node_type = 'corner';
                        point_data.tangency_broken = false; // Not applicable for corner
                        // Set CPs to the node's own position for straight lines
                        point_data.cp_in_x = point_data.x;
                        point_data.cp_in_y = point_data.y;
                        point_data.cp_out_x = point_data.x;
                        point_data.cp_out_y = point_data.y;
                        if (balloon.control_point_handles) {
                            balloon.control_point_handles.destroy();
                            balloon.control_point_handles = null;
                        }
                        needs_path_redraw = true;
                        break;
                }

                if (handle) {
                    handle.fill(this.get_anchor_handle_color(point_data));
                }

                if (needs_path_redraw || point_data.node_type !== previous_node_type || point_data.tangency_broken !== previous_tangency_broken) {
                    const new_path = this.generate_balloon_path_from_points(points_array);
                    balloon.shape.data(new_path);
                    this.push_to_history('edit_balloon_node_type_via_button');
                }
                
                balloon.getLayer()?.batchDraw();
            }
        },

        /**
         * Recalculates control points locally for a node and its neighbors.
         * @param {Array} points - The full array of points for the balloon.
         * @param {number} index - The index of the node that was added or changed.
         */
        recalculate_local_control_points(points, index) {
            const main_points = points.filter(p => p.type === 'main');
            if (main_points.length < 2) return;

            // Find the index within main_points corresponding to the actual index
            let main_index = -1;
            const actual_node = points[index]; // Node at the original index
            for(let i = 0; i < main_points.length; i++) {
                if (main_points[i] === actual_node) {
                     main_index = i;
                     break;
                }
            }

            if (main_index === -1) {
                console.warn("Could not find main_index for local recalculation at index:", index, ". Recalculating all.");
                // Fallback: recalculate all points (less efficient but safe)
                const recalculated_all = this.calculate_initial_control_points(points);
                // Manually update the original points array with recalculated values
                points.forEach((p, idx) => {
                    const recalc_p = recalculated_all.find(rp => rp === p); // Find by reference
                    if (recalc_p) {
                        Object.assign(p, recalc_p); // Update properties
                    }
                });
                return;
            }


            const len = main_points.length;
            // Indices in the main_points array to recalculate
            const indices_to_recalc = [
                (main_index - 1 + len) % len, // Previous main point
                main_index,                   // Current main point (the one added/changed)
                (main_index + 1) % len        // Next main point
            ];

            const unique_indices = [...new Set(indices_to_recalc)];
            const handle_factor = 0.38;

            unique_indices.forEach(idx => {
                const curr = main_points[idx];

                // Skip corner nodes, but ensure their CPs are reset
                if (curr.node_type === 'corner') {
                    curr.cp_in_x = curr.x; curr.cp_in_y = curr.y;
                    curr.cp_out_x = curr.x; curr.cp_out_y = curr.y;
                    curr.tangency_broken = false; // Corners don't have tangency
                    return;
                    }

                // Find the actual previous and next MAIN points in the original array
                let prev_main_node = null;
                for (let i = 1; i < points.length; i++) {
                    const check_idx = (index - i + points.length) % points.length;
                    if (points[check_idx].type === 'main') {
                        prev_main_node = points[check_idx];
                        break;
                    }
                }

                let next_main_node = null;
                for (let i = 1; i < points.length; i++) {
                    const check_idx = (index + i) % points.length;
                    if (points[check_idx].type === 'main') {
                        next_main_node = points[check_idx];
                        break;
                    }
                }

                // Need valid neighbors to calculate tangent
                if (!prev_main_node || !next_main_node) return;


                const dx = next_main_node.x - prev_main_node.x;
                const dy = next_main_node.y - prev_main_node.y;

                if (dx === 0 && dy === 0) { // Handle coincident neighbors
                    curr.cp_in_x = curr.x; curr.cp_in_y = curr.y;
                    curr.cp_out_x = curr.x; curr.cp_out_y = curr.y;
                    return;
                }


                const angle = Math.atan2(dy, dx);
                const dist_prev = Math.hypot(curr.x - prev_main_node.x, curr.y - prev_main_node.y);
                const dist_next = Math.hypot(next_main_node.x - curr.x, next_main_node.y - curr.y);
                const handle_factor = 0.38; // Use consistent factor

                const cp_in_dist = dist_prev * handle_factor;
                curr.cp_in_x = curr.x - Math.cos(angle) * cp_in_dist;
                curr.cp_in_y = curr.y - Math.sin(angle) * cp_in_dist;

                const cp_out_dist = dist_next * handle_factor;
                curr.cp_out_x = curr.x + Math.cos(angle) * cp_out_dist;
                curr.cp_out_y = curr.y + Math.sin(angle) * cp_out_dist;

                // Reset tangency broken flag for recalculated smooth nodes
                curr.tangency_broken = false;
            });
             // This function modifies the objects within the 'points' array directly.
        },

        // Debounced history push for frequent edits like dragging nodes/CPs
        debounced_push_history(action_name) {
            if (!this.history_pending_balloon_edit) return; // Only push if an edit actually happened

            clearTimeout(this.history_timer_balloon_edit);
            this.history_timer_balloon_edit = setTimeout(() => {
                if (this.history_pending_balloon_edit) { // Double check flag before pushing
                    this.push_to_history(action_name);
                    this.history_pending_balloon_edit = false; // Reset flag after pushing
                }
            }, 500); // Wait 500ms after the last drag move
        },

        /**
         * Calculates the coordinates of a point on a cubic Bezier curve.
         * p0, p1, p2, p3 are objects with x, y properties.
         * t is the parameter (0 to 1).
         */
        get_bezier_point(t, p0, p1, p2, p3) {
            const u = 1 - t;
            const tt = t * t;
            const uu = u * u;
            const uuu = uu * u;
            const ttt = tt * t;

            const p = { x: 0, y: 0 };
            p.x = uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x;
            p.y = uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y;

            return p;
        },

        /**
         * Subdivides a cubic Bezier curve at parameter t using De Casteljau's algorithm.
         * Returns an object containing the control points for the two new curves.
         * q0, q1, q2, q3 define the first curve (from t=0 to t=t_original).
         * r0, r1, r2, r3 define the second curve (from t=t_original to t=1).
         * Note: q0 = p0, q3 = P, r0 = P, r3 = p3, where P is the point at t.
         */
        subdivide_bezier(p0, p1, p2, p3, t) {
            const u = 1 - t;

            // First level of interpolation
            const q1 = { x: u * p0.x + t * p1.x, y: u * p0.y + t * p1.y };
            const q2 = { x: u * p1.x + t * p2.x, y: u * p1.y + t * p2.y };
            const q3 = { x: u * p2.x + t * p3.x, y: u * p2.y + t * p3.y };

            // Second level of interpolation
            const r0 = { x: u * q1.x + t * q2.x, y: u * q1.y + t * q2.y }; // This is cp_out for P in the first segment
            const r1 = { x: u * q2.x + t * q3.x, y: u * q2.y + t * q3.y }; // This is cp_in for p3 in the second segment

            // Third level of interpolation (the point P itself)
            const P = { x: u * r0.x + t * r1.x, y: u * r0.y + t * r1.y }; // q3 = P, r0 = P

            return {
                q0: p0,    // Start of first curve
                q1: q1,    // cp_out for p0 in the first curve
                q2: r0,    // cp_in for P in the first curve
                q3: P,     // End of first curve (the split point)

                r0: P,     // Start of second curve (the split point)
                r1: r1,    // cp_out for P in the second curve
                r2: q3,    // cp_in for p3 in the second curve
                r3: p3     // End of second curve
            };
        },

        // Helper to recalculate CPs for a single node when tangency is restored or type changes to smooth
        _recalculate_single_node_cps(points, index) {
             const main_points = points.filter(p => p.type === 'main');
             if (main_points.length < 2) return;

             let main_index = -1;
             const actual_node = points[index];
             for(let i = 0; i < main_points.length; i++) {
                 if (main_points[i] === actual_node) {
                      main_index = i;
                      break;
                 }
             }
             if (main_index === -1) return; // Node not found in main points

             const len = main_points.length;
             const curr = main_points[main_index]; // The node to recalculate

             // Skip if it's a corner node (shouldn't happen if called correctly)
             if (curr.node_type === 'corner') return;

             const prev = main_points[(main_index - 1 + len) % len];
             const next = main_points[(main_index + 1) % len];

             const dx = next.x - prev.x;
             const dy = next.y - prev.y;

             if (dx === 0 && dy === 0) { // Handle coincident neighbors
                 curr.cp_in_x = curr.x; curr.cp_in_y = curr.y;
                 curr.cp_out_x = curr.x; curr.cp_out_y = curr.y;
                 return;
             }

             const angle = Math.atan2(dy, dx);
             const dist_prev = Math.hypot(curr.x - prev.x, curr.y - prev.y);
             const dist_next = Math.hypot(next.x - curr.x, next.y - curr.y);
             const handle_factor = 0.38; // Use consistent factor

             const cp_in_dist = dist_prev * handle_factor;
             curr.cp_in_x = curr.x - Math.cos(angle) * cp_in_dist;
             curr.cp_in_y = curr.y - Math.sin(angle) * cp_in_dist;

             const cp_out_dist = dist_next * handle_factor;
             curr.cp_out_x = curr.x + Math.cos(angle) * cp_out_dist;
             curr.cp_out_y = curr.y + Math.sin(angle) * cp_out_dist;

             // Ensure tangency is marked as not broken
             curr.tangency_broken = false;
        },

        /**
         * Adjusts the control points connecting two nodes after a node between them was deleted.
         * Recalculates based on new neighbors, respecting tangency.
         * Modifies the points array directly.
         * @param {Array} points - The array of points AFTER the node was deleted.
         * @param {number} prev_node_idx - Index of the node before the deleted one.
         * @param {number} next_node_idx - Index of the node after the deleted one.
         */
        _adjust_neighbor_cps_after_delete(points, prev_node_idx, next_node_idx) {
            const len = points.length;
            if (len < 2 || prev_node_idx < 0 || prev_node_idx >= len || next_node_idx < 0 || next_node_idx >= len) {
                return;
            }

            const prev_node = points[prev_node_idx];
            const next_node = points[next_node_idx];
            const handle_factor = 0.38; // Consistent handle factor

            // Find the nodes around the neighbors *after* deletion
            const prev_prev_node_idx = (prev_node_idx - 1 + len) % len;
            const next_next_node_idx = (next_node_idx + 1) % len;

            // Avoid self-reference if only 2 points remain
            const prev_prev_node = (prev_prev_node_idx === next_node_idx) ? prev_node : points[prev_prev_node_idx];
            const next_next_node = (next_next_node_idx === prev_node_idx) ? next_node : points[next_next_node_idx];

            // --- Recalculate CPs for prev_node ---
            if (prev_node.node_type === 'smooth') {
                const tangent_dx_prev = next_node.x - prev_prev_node.x;
                const tangent_dy_prev = next_node.y - prev_prev_node.y;

                if (Math.abs(tangent_dx_prev) > 1e-6 || Math.abs(tangent_dy_prev) > 1e-6) {
                    const angle_prev = Math.atan2(tangent_dy_prev, tangent_dx_prev);
                    const dist_next = Math.hypot(next_node.x - prev_node.x, next_node.y - prev_node.y);

                    // Update cp_out towards next_node
                    const cp_out_dist_prev = dist_next * handle_factor;
                    prev_node.cp_out_x = prev_node.x + Math.cos(angle_prev) * cp_out_dist_prev;
                    prev_node.cp_out_y = prev_node.y + Math.sin(angle_prev) * cp_out_dist_prev;

                    // If tangency is not broken, update cp_in symmetrically
                    if (!prev_node.tangency_broken) {
                         const dist_prev_prev = Math.hypot(prev_node.x - prev_prev_node.x, prev_node.y - prev_prev_node.y);
                         const cp_in_dist_prev = dist_prev_prev * handle_factor;
                         prev_node.cp_in_x = prev_node.x - Math.cos(angle_prev) * cp_in_dist_prev;
                         prev_node.cp_in_y = prev_node.y - Math.sin(angle_prev) * cp_in_dist_prev;
                    }
                } else { // Handle coincident points defining tangent
                    prev_node.cp_out_x = prev_node.x; prev_node.cp_out_y = prev_node.y;
                    if (!prev_node.tangency_broken) { // Also reset cp_in if symmetric
                        prev_node.cp_in_x = prev_node.x; prev_node.cp_in_y = prev_node.y;
                    }
                }

            } else { // Ensure corner node CPs are at the node
                 prev_node.cp_out_x = prev_node.x; prev_node.cp_out_y = prev_node.y;
                 // prev_node.cp_in_x = prev_node.x; prev_node.cp_in_y = prev_node.y; // cp_in is less relevant for the segment *after* it
            }

            // --- Recalculate CPs for next_node ---
            if (next_node.node_type === 'smooth') {
                const tangent_dx_next = next_next_node.x - prev_node.x;
                const tangent_dy_next = next_next_node.y - prev_node.y;

                if (Math.abs(tangent_dx_next) > 1e-6 || Math.abs(tangent_dy_next) > 1e-6) {
                    const angle_next = Math.atan2(tangent_dy_next, tangent_dx_next);
                    const dist_prev = Math.hypot(next_node.x - prev_node.x, next_node.y - prev_node.y);

                    // Update cp_in towards prev_node
                    const cp_in_dist_next = dist_prev * handle_factor;
                    next_node.cp_in_x = next_node.x - Math.cos(angle_next) * cp_in_dist_next;
                    next_node.cp_in_y = next_node.y - Math.sin(angle_next) * cp_in_dist_next;

                    // If tangency is not broken, update cp_out symmetrically
                    if (!next_node.tangency_broken) {
                        const dist_next_next = Math.hypot(next_next_node.x - next_node.x, next_next_node.y - next_node.y);
                        const cp_out_dist_next = dist_next_next * handle_factor;
                        next_node.cp_out_x = next_node.x + Math.cos(angle_next) * cp_out_dist_next;
                        next_node.cp_out_y = next_node.y + Math.sin(angle_next) * cp_out_dist_next;
                    }
                } else { // Handle coincident points defining tangent
                     next_node.cp_in_x = next_node.x; next_node.cp_in_y = next_node.y;
                     if (!next_node.tangency_broken) { // Also reset cp_out if symmetric
                        next_node.cp_out_x = next_node.x; next_node.cp_out_y = next_node.y;
                     }
                }
            } else { // Ensure corner node CPs are at the node
                 // next_node.cp_out_x = next_node.x; next_node.cp_out_y = next_node.y; // cp_out is less relevant for the segment *before* it
                 next_node.cp_in_x = next_node.x; next_node.cp_in_y = next_node.y;
            }
        },
        /**
         * Applies the current group scale to all balloon points and resets group scale to 1.
         * Ensures stroke width is restored to the user-defined value.
         * @param {Object} balloon - The balloon object.
         */
        bake_balloon_transform(balloon) {
            if (!balloon || !balloon.shape) return;

            const group = balloon;
            const scale_x = group.scaleX();
            const scale_y = group.scaleY();

            if (scale_x === 1 && scale_y === 1) return;

            const points = group.getAttr('balloon_points');
            if (points) {
                points.forEach(point => {
                    point.x *= scale_x;
                    point.y *= scale_y;
                    if (point.cp_in_x !== undefined) point.cp_in_x *= scale_x;
                    if (point.cp_in_y !== undefined) point.cp_in_y *= scale_y;
                    if (point.cp_out_x !== undefined) point.cp_out_x *= scale_x;
                    if (point.cp_out_y !== undefined) point.cp_out_y *= scale_y;
                });
            }

            if (group.texts && Array.isArray(group.texts)) {
                group.texts.forEach(text_node => {
                    text_node.x(text_node.x() * scale_x);
                    text_node.y(text_node.y() * scale_y);

                    const custom_box = text_node.getAttr('custom_box_for_recenter');
                    if (custom_box) {
                        custom_box.x *= scale_x;
                        custom_box.y *= scale_y;
                        custom_box.width *= scale_x;
                        custom_box.height *= scale_y;
                        text_node.setAttr('custom_box_for_recenter', custom_box);
                    }
                });
            } else if (group.text) {
                group.text.x(group.text.x() * scale_x);
                group.text.y(group.text.y() * scale_y);

                // Update custom_box_for_recenter for individual balloon text if it exists
                // This makes behavior consistent with merged balloons
                const text_custom_box = group.text.getAttr('custom_box_for_recenter');
                if (text_custom_box) {
                    text_custom_box.x *= scale_x;
                    text_custom_box.y *= scale_y;
                    text_custom_box.width *= scale_x;
                    text_custom_box.height *= scale_y;
                    group.text.setAttr('custom_box_for_recenter', text_custom_box);
                }

                // Keep updating group attributes for backwards compatibility
                if (group.getAttr('custom_text_box_x') !== undefined) {
                    group.setAttr('custom_text_box_x', group.getAttr('custom_text_box_x') * scale_x);
                }
                if (group.getAttr('custom_text_box_y') !== undefined) {
                    group.setAttr('custom_text_box_y', group.getAttr('custom_text_box_y') * scale_y);
                }
                if (group.getAttr('custom_text_box_width') !== undefined) {
                    group.setAttr('custom_text_box_width', group.getAttr('custom_text_box_width') * scale_x);
                }
                if (group.getAttr('custom_text_box_height') !== undefined) {
                    group.setAttr('custom_text_box_height', group.getAttr('custom_text_box_height') * scale_y);
                }
            }
            
            // Scale thought bubble positions and radii if they exist
            const thought_bubbles = group.getAttr('thought_bubbles');
            if (thought_bubbles && thought_bubbles.length > 0) {
                thought_bubbles.forEach(bubble_array => {
                    bubble_array.forEach(bubble => {
                        bubble.x *= scale_x;
                        bubble.y *= scale_y;
                        // Store separate X and Y radii for elliptical deformation
                        if (!bubble.radius_x) bubble.radius_x = bubble.radius;
                        if (!bubble.radius_y) bubble.radius_y = bubble.radius;
                        bubble.radius_x *= scale_x;
                        bubble.radius_y *= scale_y;
                        // Update main radius for backwards compatibility
                        bubble.radius = Math.max(bubble.radius_x, bubble.radius_y);
                    });
                });
                group.setAttr('thought_bubbles', thought_bubbles);
                
                // Update thought bubble node positions and radii
                this.update_thought_bubble_node_transforms(group, thought_bubbles);
            }

            group.scaleX(1);
            group.scaleY(1);

            const original_stroke_width = group.getAttr('original_stroke_width');
            const final_stroke_width = (original_stroke_width !== undefined && original_stroke_width !== null) ? 
                original_stroke_width : (this.balloon_default_stroke_width || this.preferences.balloon_defaults.stroke_width);
            group.shape.strokeWidth(final_stroke_width);
            
            if (points) {
                const new_path = this.generate_balloon_path_from_points(points);
                group.shape.data(new_path);
            }
            
            // Update thought bubbles if they exist
            this.update_balloon_shape_with_thought_bubbles(group);
        },

        update_thought_bubble_node_transforms(balloon, thought_bubbles) {
            if (!balloon || !thought_bubbles) return;
            
            const thought_bubble_nodes = balloon.find('.thought_bubble');
            
            thought_bubbles.forEach((bubble_array, array_index) => {
                const sorted_bubbles = [...bubble_array].sort((a, b) => a.radius - b.radius);
                
                sorted_bubbles.forEach((bubble, bubble_index) => {
                    // Find the corresponding node
                    const matching_node = thought_bubble_nodes.find(node => 
                        node.getAttr('bubble_array_index') === array_index && 
                        node.getAttr('bubble_index') === bubble_index
                    );
                    
                    if (matching_node) {
                        matching_node.x(bubble.x);
                        matching_node.y(bubble.y);
                        matching_node.radiusX(bubble.radius_x || bubble.radius);
                        matching_node.radiusY(bubble.radius_y || bubble.radius);
                    }
                });
            });
        },

        set_pointer_type(type) {
            this.active_pointer_type = type;
            this.status_message = this.get_t('status.selected_pointer_style', { type });
        },
        
        //! Merge system
        async merge_shapes(balloon_groups) {
            // Convert single balloon group to array for backward compatibility
            if (!Array.isArray(balloon_groups)) {
                balloon_groups = [balloon_groups];
            }

            if (balloon_groups.length < 2) {
                console.error("merge_shapes requires at least 2 balloon groups.");
                return { d_attribute: null, error: 'insufficient_balloons' };
            }

            // Ensure Paper.js is loaded and initialized globally once
            if (!paper.project || !paper.project.view) { 
                const temp_canvas = document.createElement('canvas');
                temp_canvas.width = 1; 
                temp_canvas.height = 1;
                paper.setup(temp_canvas); 
            }
            paper.project.clear();
            
            const paper_paths = [];
            let united_path = null;

            try {
                // Create paper paths for all balloon groups
                for (let i = 0; i < balloon_groups.length; i++) {
                    const balloon_group = balloon_groups[i];
                    const svg_data = balloon_group.shape.data();

                    if (typeof svg_data !== 'string' || svg_data.trim() === '') {
                        console.error(`Invalid or empty svg_data for balloon ${i} in Paper.js Path creation.`);
                        return { d_attribute: null, error: `invalid_svg_data_${i}` };
                    }
                    // Debug: Check if this is a complex path with multiple subpaths
                    const subpath_count = (svg_data.match(/M/g) || []).length;
                    if (subpath_count > 1) {
                        console.log(`Processing complex path with ${subpath_count} subpaths for balloon ${i}`);
                    }

                    // For complex compound paths, create a compound path in Paper.js
                    let paper_path;
                    if (subpath_count > 1) {
                        // Split the compound path into individual paths
                        const individual_paths = [];
                        const path_segments = svg_data.split(/(?=M)/).filter(segment => segment.trim());
                        
                        for (const segment of path_segments) {
                            if (segment.trim()) {
                                const individual_paper_path = new paper.Path(segment.trim());
                                if (!individual_paper_path.isEmpty()) {
                                    individual_paths.push(individual_paper_path);
                                }
                            }
                        }
                        
                        // Create a compound path
                        if (individual_paths.length > 0) {
                            paper_path = new paper.CompoundPath({ children: individual_paths });
                        } else {
                            paper_path = new paper.Path(svg_data);
                        }
                    } else {
                        // Simple path
                        paper_path = new paper.Path(svg_data);
                    }
                    if (paper_path.isEmpty()) {
                        console.warn(`Paper path ${i} is empty after creation.`);
                        paper_paths.forEach(p => p.remove && p.remove());
                        return { d_attribute: null, error: `empty_paper_path_${i}` };
                    }

                    // Apply transformation matrix
                    const transform_matrix = balloon_group.getAbsoluteTransform().getMatrix();
                    const paper_matrix = new paper.Matrix(
                        transform_matrix[0], transform_matrix[1], transform_matrix[2],
                        transform_matrix[3], transform_matrix[4], transform_matrix[5]
                    );
                    paper_path.transform(paper_matrix);
                    
                    paper_paths.push(paper_path);
                }

                // Check for intersections between all paths
                let has_intersection = false;
                for (let i = 0; i < paper_paths.length - 1; i++) {
                    for (let j = i + 1; j < paper_paths.length; j++) {
                        if (paper_paths[i].intersects(paper_paths[j])) {
                            has_intersection = true;
                            break;
                        }
                    }
                    if (has_intersection) break;
                }

                if (!has_intersection) {
                    console.warn("No intersections found between balloon groups. Merge operation aborted.");
                    paper_paths.forEach(p => p.remove && p.remove());
                    return { d_attribute: null, error: 'no_intersection' };
                }                // Perform sequential union operations
                united_path = paper_paths[0];
                for (let i = 1; i < paper_paths.length; i++) {
                    const next_united = united_path.unite(paper_paths[i]);
                    
                    if (!next_united || next_united.isEmpty()) {
                        console.warn(`Union operation ${i} resulted in an empty or invalid path.`);
                        paper_paths.forEach(p => p.remove && p.remove());
                        if (united_path && united_path.remove) united_path.remove();
                        return { d_attribute: null, error: `empty_union_result_${i}` };
                    }
                    
                    // Remove the previous united path if it's not the original first path
                    if (i > 1 && united_path && united_path.remove) {
                        united_path.remove();
                    }
                    
                    united_path = next_united;
                }

                const united_bounds_scaled = united_path.bounds;
                if (!united_bounds_scaled) {
                    console.error("Could not get bounds from united Paper.js path before descaling.");
                    paper_paths.forEach(p => p.remove && p.remove());
                    if (united_path && united_path.remove) united_path.remove();
                    return { d_attribute: null, error: 'bounds_error_scaled' };
                }
                const new_shape_abs_x = united_bounds_scaled.x;
                const new_shape_abs_y = united_bounds_scaled.y;

                const stage_scale_x = this.stage ? this.stage.scaleX() : 1;
                const stage_scale_y = this.stage ? this.stage.scaleY() : 1;

                if (stage_scale_x !== 1 || stage_scale_y !== 1) {
                    if (united_path.bounds && united_path.bounds.center) { 
                        const inverse_scale_matrix = new paper.Matrix();
                        // Scale relative to its own center to minimize displacement during descaling
                        inverse_scale_matrix.scale(1 / stage_scale_x, 1 / stage_scale_y, united_path.bounds.center);
                        united_path.transform(inverse_scale_matrix);
                    } else {
                        // Fallback: scale from origin if bounds are not available (less ideal)
                        united_path.scale(1 / stage_scale_x, 1 / stage_scale_y);
                    }
                }
                
                const paper_bounds_descaled = united_path.bounds;
                if (!paper_bounds_descaled) {
                    console.error("Could not get bounds from united Paper.js path after descaling.");
                    paper_paths.forEach(p => p.remove && p.remove());
                    if (united_path && united_path.remove) united_path.remove();
                    return { d_attribute: null, error: 'bounds_error_descaled' };
                }
                
                // Translate the path so its top-left is at (0,0) for the d_attribute
                united_path.translate(new paper.Point(-paper_bounds_descaled.x, -paper_bounds_descaled.y)); 
                
                const full_svg_string = united_path.exportSVG({ asString: true });
                const match = full_svg_string.match(/d="([^"]+)"/);
                if (match && match[1]) {
                    const d_attribute_data = match[1];
                    return {
                        d_attribute: d_attribute_data,
                        abs_x: new_shape_abs_x, 
                        abs_y: new_shape_abs_y
                    };
                } else {
                    console.error("Could not extract d attribute from Paper.js SVG output:", full_svg_string);
                    return { d_attribute: null, error: 'd_extract_failed' };
                }

            } catch (error) {
                console.error("Error during Paper.js path operation in merge_shapes:", error.message, error.stack);
                balloon_groups.forEach((group, i) => {
                    if (group && group.shape) {
                        console.error(`Input svg_data${i} (from group${i}):`, group.shape.data());
                    }
                });
                return { d_attribute: null, error: 'exception_in_merge' };
            } finally {
                // Clean up all paper paths
                paper_paths.forEach(p => p.remove && p.remove());
                if (united_path && united_path.remove && !paper_paths.includes(united_path)) {
                    united_path.remove();
                }
            }
        },
        _convert_path_data_to_balloon_points(path_d_string) {
            if (typeof path_d_string !== 'string' || path_d_string.trim() === '') {
                console.warn("_convert_path_data_to_balloon_points: path_d_string is invalid or empty. Value:", path_d_string);
                return [];
            }

            let temp_path_for_parsing;
            let segments;
            try {
                temp_path_for_parsing = new Konva.Path({ data: path_d_string });
                segments = temp_path_for_parsing.dataArray;
            } catch (e) {
                console.error("Error creating Konva.Path in _convert_path_data_to_balloon_points:", e, path_d_string);
                if (temp_path_for_parsing && typeof temp_path_for_parsing.destroy === 'function') temp_path_for_parsing.destroy();
                return [];
            }

            const balloon_points = [];
            let current_x = 0;
            let current_y = 0;
            let current_subpath_start = 0; // Track start of current subpath for multiple contours

            // First pass: Extract anchor points and control points from SVG segments
            for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];
                const command = seg.command.toUpperCase();
                const pts = seg.points;
                const prev_anchor_idx = balloon_points.length - 1;
                let prev_anchor = prev_anchor_idx >= 0 ? balloon_points[prev_anchor_idx] : null;

                switch (command) {
                    case 'M':
                        current_x = pts[0];
                        current_y = pts[1];
                        current_subpath_start = balloon_points.length; // Mark start of new subpath
                        balloon_points.push({
                            x: current_x, y: current_y, type: 'main',
                            cp_in_x: current_x, cp_in_y: current_y,
                            cp_out_x: current_x, cp_out_y: current_y,
                            node_type: 'smooth', tangency_broken: false,
                            subpath_start: current_subpath_start // Mark subpath boundaries
                        });
                        break;
                    case 'L':
                        if (prev_anchor) { 
                            prev_anchor.cp_out_x = prev_anchor.x; 
                            prev_anchor.cp_out_y = prev_anchor.y;
                            // Tentatively set previous as corner, will be refined
                            prev_anchor.node_type = 'corner'; 
                            prev_anchor.tangency_broken = true;
                        }
                        current_x = pts[0];
                        current_y = pts[1];
                        balloon_points.push({
                            x: current_x, y: current_y, type: 'main',
                            cp_in_x: current_x, cp_in_y: current_y,   
                            cp_out_x: current_x, cp_out_y: current_y, 
                            node_type: 'corner', tangency_broken: true // L segments create corners
                        });
                        break;
                    case 'C':
                        if (prev_anchor) {
                            prev_anchor.cp_out_x = pts[0]; 
                            prev_anchor.cp_out_y = pts[1]; 
                            // If prev_anchor was M, it becomes smooth. If L, it stays corner.
                            if (segments[i-1] && segments[i-1].command.toUpperCase() === 'M') {
                                prev_anchor.node_type = 'smooth';
                                prev_anchor.tangency_broken = false;
                            }
                        }
                        current_x = pts[4]; 
                        current_y = pts[5]; 
                        balloon_points.push({
                            x: current_x, y: current_y, type: 'main',
                            cp_in_x: pts[2], cp_in_y: pts[3],     
                            cp_out_x: current_x, cp_out_y: current_y, // Placeholder for cp_out
                            node_type: 'smooth', tangency_broken: false // Default for curve end
                        });
                        break;
                    case 'Q': // Convert Quadratic to Cubic for consistent CP structure
                        const p0_q = prev_anchor ? { x: prev_anchor.x, y: prev_anchor.y } : { x: current_x, y: current_y };
                        const p1_q = { x: pts[0], y: pts[1] }; 
                        const p2_q = { x: pts[2], y: pts[3] }; 

                        const cp1_c_x = p0_q.x + 2/3 * (p1_q.x - p0_q.x);
                        const cp1_c_y = p0_q.y + 2/3 * (p1_q.y - p0_q.y);
                        const cp2_c_x = p2_q.x + 2/3 * (p1_q.x - p2_q.x);
                        const cp2_c_y = p2_q.y + 2/3 * (p1_q.y - p2_q.y);

                        if (prev_anchor) {
                            prev_anchor.cp_out_x = cp1_c_x;
                            prev_anchor.cp_out_y = cp1_c_y;
                            if (segments[i-1] && segments[i-1].command.toUpperCase() === 'M') {
                                prev_anchor.node_type = 'smooth';
                                prev_anchor.tangency_broken = false;
                            }
                        }
                        current_x = p2_q.x;
                        current_y = p2_q.y;
                        balloon_points.push({
                            x: current_x, y: current_y, type: 'main',
                            cp_in_x: cp2_c_x, cp_in_y: cp2_c_y,
                            cp_out_x: current_x, cp_out_y: current_y, 
                            node_type: 'smooth', tangency_broken: false
                        });
                        break;
                    case 'Z':
                        if (balloon_points.length > 0) {
                            // Find the start of current subpath
                            const subpath_start_idx = current_subpath_start;
                            const first_node = balloon_points[subpath_start_idx];
                            let last_node = balloon_points[balloon_points.length - 1];

                            // Only merge if last and first of current subpath are very close
                            if (Math.abs(last_node.x - first_node.x) < 0.001 && Math.abs(last_node.y - first_node.y) < 0.001 && balloon_points.length > subpath_start_idx + 1) {
                                first_node.cp_in_x = last_node.cp_in_x;
                                first_node.cp_in_y = last_node.cp_in_y;
                                if (last_node.node_type === 'smooth') {
                                     first_node.node_type = 'smooth';
                                     first_node.tangency_broken = last_node.tangency_broken; 
                                } else { 
                                     first_node.node_type = 'corner';
                                     first_node.tangency_broken = true;
                                }
                                balloon_points.pop();
                                if (balloon_points.length > 0) {
                                   last_node = balloon_points[balloon_points.length - 1]; 
                                } else {
                                    last_node = null; 
                                }
                            }
                            
                            if (last_node && first_node) { 
                                if (segments[i-1] && segments[i-1].command.toUpperCase() === 'L') {
                                    last_node.node_type = 'corner';
                                    last_node.cp_out_x = last_node.x;
                                    last_node.cp_out_y = last_node.y;
                                    last_node.tangency_broken = true;

                                    first_node.node_type = 'corner';
                                    first_node.cp_in_x = first_node.x;
                                    first_node.cp_in_y = first_node.y;
                                    first_node.tangency_broken = true;
                                }
                            }
                        }
                        break;
                }
            }

            const epsilon = 0.001; 
            for (let k = 0; k < balloon_points.length; k++) {
                const p = balloon_points[k];
                if (p.type !== 'main') continue;

                p.cp_in_x = p.cp_in_x ?? p.x;
                p.cp_in_y = p.cp_in_y ?? p.y;
                p.cp_out_x = p.cp_out_x ?? p.x;
                p.cp_out_y = p.cp_out_y ?? p.y;

                const is_cp_in_at_anchor = Math.abs(p.cp_in_x - p.x) < epsilon && Math.abs(p.cp_in_y - p.y) < epsilon;
                const is_cp_out_at_anchor = Math.abs(p.cp_out_x - p.x) < epsilon && Math.abs(p.cp_out_y - p.y) < epsilon;

                if (is_cp_in_at_anchor && is_cp_out_at_anchor) {
                    p.node_type = 'corner';
                    p.tangency_broken = true; 
                } else { 
                    p.node_type = 'smooth';
                    
                    if (is_cp_in_at_anchor || is_cp_out_at_anchor) { 
                        p.tangency_broken = true;
                    } else { 
                        const v_in_x = p.x - p.cp_in_x; // Vector from cp_in to anchor
                        const v_in_y = p.y - p.cp_in_y;
                        const v_out_x = p.cp_out_x - p.x; // Vector from anchor to cp_out
                        const v_out_y = p.cp_out_y - p.y;

                        const len_v_in_sq = v_in_x * v_in_x + v_in_y * v_in_y;
                        const len_v_out_sq = v_out_x * v_out_x + v_out_y * v_out_y;

                        if (len_v_in_sq < epsilon * epsilon || len_v_out_sq < epsilon * epsilon) {
                            p.tangency_broken = true;
                        } else {
                            const dot_product = v_in_x * v_out_x + v_in_y * v_out_y;
                            const cos_theta = dot_product / (Math.sqrt(len_v_in_sq) * Math.sqrt(len_v_out_sq));
                            
                            // For unbroken tangency, v_in and v_out should be collinear and point in the SAME direction.
                            // So, cos_theta should be close to +1.
                            if (cos_theta > 0.99) { 
                                p.tangency_broken = false; 
                            } else {
                                p.tangency_broken = true; 
                            }
                        }
                    }
                }
            }

            if (temp_path_for_parsing && typeof temp_path_for_parsing.destroy === 'function') {
                temp_path_for_parsing.destroy();
            }
            return balloon_points;
        },
        
        //Point is intersection point
        get_pointer_base_nodes_coordinates(balloon, base_point, end_point) {
            // Ensure Paper.js is loaded and initialized globally once
            if (!paper.project || !paper.project.view) {
                const temp_canvas = document.createElement('canvas');
                temp_canvas.width = 1;
                temp_canvas.height = 1;
                paper.setup(temp_canvas);
            }
            paper.project.clear();

            const svg_data = balloon.shape.data();
            const paper_path = new paper.Path(svg_data);

            const base = new paper.Point(base_point.x, base_point.y);
            const end = new paper.Point(end_point.x, end_point.y);

            // Guide vector (from base to endpoint)
            const guide_vec = end.subtract(base);
            if (guide_vec.length === 0) {
                // If guide is null, return both points at the same location
                return { point_left: { x: base.x, y: base.y }, point_right: { x: base.x, y: base.y } };
            }

            // Perpendicular vector normalized
            const perp = new paper.Point(-guide_vec.y, guide_vec.x).normalize();
            const current_pointer_width = this.pointer_width || 12;
            const offset = current_pointer_width / 2;

            // Always use the robust geometric approach for both points
            // to ensure symmetry regardless of direction
            
            // For the left point:
            // Create temp construction point at pointer_width distance along -perp
            const temp_construction_point_left = base.add(perp.multiply(-current_pointer_width));
            
            // Create a line from this construction point to endpoint
            const line_for_left_intersection = new paper.Path.Line(temp_construction_point_left, end);
            
            // Find intersection with balloon border
            let intersections_left = paper_path.getIntersections(line_for_left_intersection);
            let point_left = null;
            
            if (intersections_left && intersections_left.length > 0) {
                // Sort by distance to construction point
                intersections_left.sort((a, b) => 
                    a.point.getDistance(temp_construction_point_left) - 
                    b.point.getDistance(temp_construction_point_left)
                );
                point_left = intersections_left[0].point;
            } else {
                // Fallback: use perpendicular offset from base
                point_left = base.add(perp.multiply(-offset));
            }
            
            // For the right point:
            // Create temp construction point at pointer_width distance along perp
            const temp_construction_point_right = base.add(perp.multiply(current_pointer_width));
            
            // Create a line from this construction point to endpoint
            const line_for_right_intersection = new paper.Path.Line(temp_construction_point_right, end);
            
            // Find intersection with balloon border
            let intersections_right = paper_path.getIntersections(line_for_right_intersection);
            let point_right = null;
            
            if (intersections_right && intersections_right.length > 0) {
                // Sort by distance to construction point
                intersections_right.sort((a, b) => 
                    a.point.getDistance(temp_construction_point_right) - 
                    b.point.getDistance(temp_construction_point_right)
                );
                point_right = intersections_right[0].point;
            } else {
                // Fallback: use perpendicular offset from base
                point_right = base.add(perp.multiply(offset));
            }
            
            // Final checks to ensure points are on the path
            if (!paper_path.contains(point_left)) {
                point_left = paper_path.getNearestPoint(point_left);
            }
            if (!paper_path.contains(point_right)) {
                point_right = paper_path.getNearestPoint(point_right);
            }

            return {
                point_left: { x: point_left.x, y: point_left.y },
                point_right: { x: point_right.x, y: point_right.y }
            };
        },


        // Add a pointer to a balloon by inserting nodes on the balloon border and connecting to an endpoint
        async handle_add_pointer(balloon, local_start, local_end, is_outward, pointer_type, intersection_result) {
            if (!balloon || !local_start || !local_end) {
                console.error("handle_add_pointer: Missing required arguments.");
                return;
            }
              // Backup current custom box settings
            const text_node = balloon.text;
            let original_custom_flag;
            if (text_node) {
                original_custom_flag = balloon.getAttr('custom_text_position');
                // If there is no custom box, save the balloon shape bounds as custom before modifying the balloon
                // Save in GROUP attributes (same as normal text editing) for consistency
                if (!original_custom_flag) {
                    const shape_box = balloon.shape.getClientRect({ relativeTo: balloon });
                    balloon.setAttr('custom_text_box_x', shape_box.x);
                    balloon.setAttr('custom_text_box_y', shape_box.y);
                    balloon.setAttr('custom_text_box_width', shape_box.width);
                    balloon.setAttr('custom_text_box_height', shape_box.height);
                    balloon.setAttr('custom_text_position', true);
                } 
                else {
                    //console.log('[DEBUG handle_add_pointer] Using existing custom box settings');
                }
            }
            

            if (pointer_type === 'triangle' || pointer_type === 'curved') {
                // Get endpoint (where the pointer should point to)
                const endpoint = { x: local_end.x, y: local_end.y };

                // Get intersection point with balloon border
                let border_point;
                if (intersection_result && intersection_result.intersection_point) {
                    border_point = intersection_result.intersection_point;
                } 
                else {
                    border_point = is_outward ? local_start : local_end;
                }

                // Calculate direction from border to endpoint
                const dir_x = endpoint.x - border_point.x;
                const dir_y = endpoint.y - border_point.y;
                const length = Math.sqrt(dir_x * dir_x + dir_y * dir_y);

                if (length < 0.001) {
                    console.warn("handle_add_pointer: Points are too close to form a valid pointer.");
                    return;
                }

                // Calculate base points using the dedicated function
                const base_points = this.get_pointer_base_nodes_coordinates(balloon, border_point, endpoint);
                const base1 = base_points.point_left;
                const base2 = base_points.point_right;

                // Insert base nodes using subdivision (no preserve_node_properties)
                this.add_point_to_balloon(balloon, base1);
                this.add_point_to_balloon(balloon, base2);

                // Get updated points and indices after insertion
                let pts = balloon.getAttr('balloon_points') || [];
                // Find indices of the two base nodes (los más cercanos a base1 y base2)
                let base1_idx = -1, base2_idx = -1;
                let min_dist1 = Infinity, min_dist2 = Infinity;
                for (let i = 0; i < pts.length; i++) {
                    const d1 = (pts[i].x - base1.x) ** 2 + (pts[i].y - base1.y) ** 2;
                    const d2 = (pts[i].x - base2.x) ** 2 + (pts[i].y - base2.y) ** 2;
                    if (d1 < min_dist1) { min_dist1 = d1; base1_idx = i; }
                    if (d2 < min_dist2) { min_dist2 = d2; base2_idx = i; }
                }
                // Eliminar nodos intermedios (sin incluir los extremos), considerando el array circular
                let from = base1_idx;
                let to = base2_idx;
                const n = pts.length;

                
                if (from !== to) {
                    // Remove all intermediate nodes between from and to (circular array)
                    let idxs_to_remove = [];
                    let idx = (from + 1) % n;
                    while (idx !== to) {
                        idxs_to_remove.push(idx);
                        idx = (idx + 1) % n;
                    }
                    // Remove in descending order to avoid index shift
                    idxs_to_remove.sort((a, b) => b - a);
                    for (const remove_idx of idxs_to_remove) {
                        pts.splice(remove_idx, 1);
                    }
                }

                // Insertar endpoint entre los dos nodos base
                const endpoint_node = {
                    x: endpoint.x, y: endpoint.y, type: 'main', node_type: 'corner',
                    tangency_broken: true,
                    cp_in_x: endpoint.x, cp_in_y: endpoint.y,
                    cp_out_x: endpoint.x, cp_out_y: endpoint.y,
                    is_non_naturalizable: true // Mark endpoint as non-naturalizable
                };
                pts.splice(from + 1, 0, endpoint_node);

                // Ajustar handles de los nodos base para que el handle interno apunte al endpoint (línea recta)
                // y marcar tangency_broken: true en ambos nodos base
                // Identificar ambos nodos base tras la inserción y el endpoint
                // Buscar los dos nodos más cercanos a base1 y base2
                const find_closest_idx = (pt) => {
                    let idx = -1, min_dist = Infinity;
                    for (let i = 0; i < pts.length; i++) {
                        const d = (pts[i].x - pt.x) ** 2 + (pts[i].y - pt.y) ** 2;
                        if (d < min_dist) { min_dist = d; idx = i; }
                    }
                    return idx;
                };
                const idx_base1 = find_closest_idx(base1);
                const idx_base2 = find_closest_idx(base2);
                const idx_endpoint = find_closest_idx(endpoint);

                // Para cada nodo base, forzar tangency_broken y handle interno sobre el nodo
                if (idx_base1 !== -1 && idx_endpoint !== -1) {
                    // Mark base1 as non-naturalizable
                    pts[idx_base1].is_non_naturalizable = true;
                    // Si el endpoint está después de base1, el OUT de base1 apunta al endpoint
                    pts[idx_base1].cp_out_x = pts[idx_base1].x;
                    pts[idx_base1].cp_out_y = pts[idx_base1].y;
                    pts[idx_base1].tangency_broken = true;
                }
                if (idx_base2 !== -1 && idx_endpoint !== -1) {
                    // Mark base2 as non-naturalizable
                    pts[idx_base2].is_non_naturalizable = true;
                    // Si el endpoint está antes de base2, el IN de base2 apunta al endpoint
                    pts[idx_base2].cp_in_x = pts[idx_base2].x;
                    pts[idx_base2].cp_in_y = pts[idx_base2].y;
                    pts[idx_base2].tangency_broken = true;
                }
                
                // Actualizar atributos y path
                balloon.setAttr('balloon_points', pts);
                balloon.shape.data(this.generate_balloon_path_from_points(pts));
                  // Apply curvature for curved pointer type
                if (pointer_type === 'curved') {
                    // Only adjust interior tangencies of base nodes
                    const curve_distance = length * 0.65; // Distance along the line to endpoint
                    const curve_offset = length * 0.25; // Perpendicular curvature offset
                      

                    // Calculate balloon center for side detection (use balloon points, BEFORE adding the pointer)
                    const original_balloon_points = balloon.getAttr('balloon_points') || [];
                    // Filter out the newly added pointer points (they don't have proper IDs yet)
                    const main_balloon_points = original_balloon_points.filter(pt => !pt.is_non_naturalizable);
                    let min_x = Infinity, max_x = -Infinity;
                    main_balloon_points.forEach(pt => {
                        min_x = Math.min(min_x, pt.x);
                        max_x = Math.max(max_x, pt.x);
                    });
                    const balloon_center_x = (min_x + max_x) / 2;
                    
                    // Calculate pointer base center for side detection (only base nodes, not endpoint)
                    const pointer_base_center_x = (base1.x + base2.x) / 2;
                    
                    // Determine if pointer BASE is on left side of balloon (ignore endpoint position)
                    const is_on_left_side = pointer_base_center_x < balloon_center_x;
                    
                    // Determine reflection: by default reflect on left side, but invert if reflect mode is enabled
                    const should_reflect = this.curved_reflect_mode ? !is_on_left_side : is_on_left_side;
                    
                    // Calculate perpendicular direction for curvature (use base to endpoint direction)
                    const direction_to_endpoint = {
                        x: endpoint.x - pointer_base_center_x,
                        y: endpoint.y - ((base1.y + base2.y) / 2)
                    };
                    const dir_length = Math.sqrt(direction_to_endpoint.x ** 2 + direction_to_endpoint.y ** 2);
                    const normalized_dir = {
                        x: direction_to_endpoint.x / dir_length,
                        y: direction_to_endpoint.y / dir_length
                    };

                    // Perpendicular vector for curvature (reflect if needed)
                    const perpendicular = {
                        x: should_reflect ? normalized_dir.y : -normalized_dir.y,
                        y: should_reflect ? -normalized_dir.x : normalized_dir.x
                    };
                    
                    // Adjust base1 interior tangency
                    if (idx_base1 !== -1) {
                        const base1_dir = {
                            x: endpoint.x - base1.x,
                            y: endpoint.y - base1.y
                        };
                        const base1_length = Math.sqrt(base1_dir.x ** 2 + base1_dir.y ** 2);
                        const base1_normalized = {
                            x: base1_dir.x / base1_length,
                            y: base1_dir.y / base1_length
                        };
                        
                        // Add curvature by offsetting perpendicular to the line
                        pts[idx_base1].cp_out_x = base1.x + base1_normalized.x * curve_distance + perpendicular.x * curve_offset;
                        pts[idx_base1].cp_out_y = base1.y + base1_normalized.y * curve_distance + perpendicular.y * curve_offset;
                    }

                    // Adjust base2 interior tangency
                    if (idx_base2 !== -1) {
                        const base2_dir = {
                            x: endpoint.x - base2.x,
                            y: endpoint.y - base2.y
                        };
                        const base2_length = Math.sqrt(base2_dir.x ** 2 + base2_dir.y ** 2);
                        const base2_normalized = {
                            x: base2_dir.x / base2_length,
                            y: base2_dir.y / base2_length
                        };
                        
                        // Add curvature by offsetting perpendicular to the line
                        pts[idx_base2].cp_in_x = base2.x + base2_normalized.x * curve_distance + perpendicular.x * curve_offset;
                        pts[idx_base2].cp_in_y = base2.y + base2_normalized.y * curve_distance + perpendicular.y * curve_offset;
                    }

                    // Update balloon attributes and redraw after curvature changes
                    balloon.setAttr('balloon_points', pts);
                    balloon.shape.data(this.generate_balloon_path_from_points(pts));
                }
                
                // Custom box is already saved in group attributes, no need to restore anything
                //console.log('[DEBUG handle_add_pointer] Pointer added, custom box preserved in group attributes');

                
                balloon.getLayer()?.batchDraw();
                this.panels_layer.batchDraw();
                this.push_to_history('add_balloon_pointer');

                // Check if auto-edit mode is enabled for curved pointers
                if (pointer_type === 'curved' && this.curved_auto_edit_mode && idx_endpoint !== -1) {
                    // Automatically enter edit mode and select the tip node
                    this.$nextTick(() => {
                        this.toggle_balloon_edit_mode(balloon);
                        this.selected_node_index = idx_endpoint;
                    });
                }
                return;
            }
            
            if (pointer_type === 'zigzag') {
                // Get endpoint (where the pointer should point to)
                const endpoint = { x: local_end.x, y: local_end.y };

                // Get intersection point with balloon border
                let border_point;
                if (intersection_result && intersection_result.intersection_point) {
                    border_point = intersection_result.intersection_point;
                } 
                else {
                    border_point = is_outward ? local_start : local_end;
                }

                // Calculate base points using existing function
                const base_points = this.get_pointer_base_nodes_coordinates(balloon, border_point, endpoint);
                const base1 = base_points.point_left;
                const base2 = base_points.point_right;

                // Calculate balloon center for inversion detection
                const original_balloon_points = balloon.getAttr('balloon_points') || [];
                const main_balloon_points = original_balloon_points.filter(pt => !pt.is_non_naturalizable);
                let min_x = Infinity, max_x = -Infinity, min_y = Infinity, max_y = -Infinity;
                main_balloon_points.forEach(pt => {
                    min_x = Math.min(min_x, pt.x);
                    max_x = Math.max(max_x, pt.x);
                    min_y = Math.min(min_y, pt.y);
                    max_y = Math.max(max_y, pt.y);
                });
                const balloon_center = {
                    x: (min_x + max_x) / 2,
                    y: (min_y + max_y) / 2
                };

                // Generate zigzag path with inversion logic
                const zigzag_points = this.generate_zigzag_svg_path(base1, base2, endpoint, this.pointer_width, balloon_center);
                
                if (!zigzag_points) {
                    console.warn('Could not generate zigzag path');
                    return;
                }

                // Add base nodes
                this.add_point_to_balloon(balloon, base1);
                this.add_point_to_balloon(balloon, base2);

                // Get updated points and find base node indices
                let pts = balloon.getAttr('balloon_points') || [];
                
                let base1_idx = -1, base2_idx = -1;
                let min_dist1 = Infinity, min_dist2 = Infinity;
                for (let i = 0; i < pts.length; i++) {
                    const d1 = (pts[i].x - base1.x) ** 2 + (pts[i].y - base1.y) ** 2;
                    const d2 = (pts[i].x - base2.x) ** 2 + (pts[i].y - base2.y) ** 2;
                    if (d1 < min_dist1) { min_dist1 = d1; base1_idx = i; }
                    if (d2 < min_dist2) { min_dist2 = d2; base2_idx = i; }
                }
                
                // Remove intermediate nodes between base nodes
                let from = base1_idx;
                let to = base2_idx;
                const n = pts.length;

                if (from !== to) {
                    let idxs_to_remove = [];
                    let idx = (from + 1) % n;
                    while (idx !== to) {
                        idxs_to_remove.push(idx);
                        idx = (idx + 1) % n;
                    }
                    idxs_to_remove.sort((a, b) => b - a);
                    for (const remove_idx of idxs_to_remove) {
                        pts.splice(remove_idx, 1);
                    }
                }

                // Convert zigzag points to proper node format and insert
                const zigzag_nodes = zigzag_points.map(point => ({
                    x: point.x,
                    y: point.y,
                    type: 'main',
                    node_type: 'corner',
                    tangency_broken: true,
                    cp_in_x: point.x,
                    cp_in_y: point.y,
                    cp_out_x: point.x,
                    cp_out_y: point.y,
                    is_non_naturalizable: true
                }));

                let insert_idx = from + 1;
                for (const node of zigzag_nodes) {
                    pts.splice(insert_idx, 0, node);
                    insert_idx++;
                }
                
                // Mark base nodes as non-naturalizable
                const find_closest_idx = (pt) => {
                    let idx = -1, min_dist = Infinity;
                    for (let i = 0; i < pts.length; i++) {
                        const d = (pts[i].x - pt.x) ** 2 + (pts[i].y - pt.y) ** 2;
                        if (d < min_dist) { min_dist = d; idx = i; }
                    }
                    return idx;
                };
                
                const idx_base1 = find_closest_idx(base1);
                const idx_base2 = find_closest_idx(base2);

                // For each base node, force tangency_broken and handle on the node (same as triangle)
                if (idx_base1 !== -1) {
                    pts[idx_base1].is_non_naturalizable = true;
                    pts[idx_base1].cp_out_x = pts[idx_base1].x;
                    pts[idx_base1].cp_out_y = pts[idx_base1].y;
                    pts[idx_base1].tangency_broken = true;
                }
                if (idx_base2 !== -1) {
                    pts[idx_base2].is_non_naturalizable = true;
                    pts[idx_base2].cp_in_x = pts[idx_base2].x;
                    pts[idx_base2].cp_in_y = pts[idx_base2].y;
                    pts[idx_base2].tangency_broken = true;
                }
                
                // Update balloon attributes and redraw
                balloon.setAttr('balloon_points', pts);
                balloon.shape.data(this.generate_balloon_path_from_points(pts));
                
                balloon.getLayer()?.batchDraw();
                this.panels_layer.batchDraw();
                this.push_to_history('add_balloon_pointer');

                return;
            }
            
            if (pointer_type === 'thought') {
                // Store thought bubble data in balloon attributes
                const bubble_count = this.thought_bubble_count || 3;
                const initial_size = this.thought_bubble_size || 18;
                
                // Calculate direction from border to endpoint
                const direction = {
                    x: local_end.x - local_start.x,
                    y: local_end.y - local_start.y
                };
                const total_length = Math.sqrt(direction.x ** 2 + direction.y ** 2);
                
                if (total_length < 0.001) {
                    console.warn("handle_add_pointer: Points are too close for thought bubbles.");
                    return;
                }
                
                // Normalize direction
                const normalized_dir = {
                    x: direction.x / total_length,
                    y: direction.y / total_length
                };
                
                // Calculate bubble positions and store them in balloon attributes
                const bubbles = [];
                for (let i = 0; i < bubble_count; i++) {
                    const progress = (i + 1) / (bubble_count + 1);
                    const position = {
                        x: local_start.x + normalized_dir.x * progress * total_length,
                        y: local_start.y + normalized_dir.y * progress * total_length
                    };
                    
                    // Size decreases as we move away from balloon
                    const size_factor = 1 - (i * 0.22); // Each bubble is 22% smaller
                    const bubble_size = Math.max(initial_size * size_factor, 4);
                      bubbles.push({
                        x: position.x,
                        y: position.y,
                        radius: bubble_size / 2,
                        radius_x: bubble_size / 2,
                        radius_y: bubble_size / 2
                    });
                }
                
                // Store bubble data in balloon attributes
                let existing_thought_bubbles = balloon.getAttr('thought_bubbles') || [];
                existing_thought_bubbles.push(bubbles);
                balloon.setAttr('thought_bubbles', existing_thought_bubbles);
                
                // Update the shape to use custom sceneFunc that draws bubbles
                this.update_balloon_shape_with_thought_bubbles(balloon);
                
                balloon.getLayer()?.batchDraw();
                this.panels_layer.batchDraw();
                this.push_to_history('add_balloon_pointer');
                
                return;
            }
            
            // If we get here, it's not a supported pointer type
            console.warn(`Pointer type "${pointer_type}" not implemented yet.`);
            return;
        },
        
        handle_merge_selected_balloons() {
            const selected_nodes = this.transformer.nodes();

            if (!selected_nodes || selected_nodes.length < 2) {
                this.status_message = this.get_t('select_at_least_two_balloons_to_merge_status');
                this.show_popup_with_content(
                    this.get_t('merge_error_title'),
                    `<div class="text-center space-y-4">
                        <div class="text-lg font-medium text-gray-100">${this.get_t('select_at_least_two_balloons_to_merge_popup')}</div>
                    </div>
                    <div class="flex justify-center mt-8">
                        <button id="ok_btn_merge_error1" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                            ${this.get_t('actions.ok')}
                        </button>
                    </div>`,
                    { type: 'warning' }
                );
                this.$nextTick(() => {
                    const ok_btn = document.getElementById('ok_btn_merge_error1');
                    if (ok_btn) ok_btn.onclick = () => this.close_popup();
                });
                return;
            }

            const balloons_to_merge = selected_nodes.filter(obj => obj.getAttr('object_type') === 'balloon' && obj.shape);

            if (balloons_to_merge.length < 2) {
                this.status_message = this.get_t('selected_objects_must_be_balloons_status');
                this.show_popup_with_content(
                    this.get_t('merge_error_title'),
                    `<div class="text-center space-y-4">
                        <div class="text-lg font-medium text-gray-100">${this.get_t('selected_objects_must_be_balloons_popup')}</div>
                    </div>
                    <div class="flex justify-center mt-8">
                        <button id="ok_btn_merge_error2" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                            ${this.get_t('actions.ok')}
                        </button>
                    </div>`,
                    { type: 'warning' }
                );
                this.$nextTick(() => {
                    const ok_btn = document.getElementById('ok_btn_merge_error2');
                    if (ok_btn) ok_btn.onclick = () => this.close_popup();
                });
                return;
            }

            const parent_group_for_merge = this.selected_panel.content_group;

            if (!parent_group_for_merge) {
                console.error("Parent group (selected_panel.content_group) is not available for positioning.");
                this.status_message = this.get_t('merge_failed_no_panel_status');
                return;
            }

            // Store text data from ALL balloons to merge
            const text_data_for_merge = [];

            balloons_to_merge.forEach(balloon_group => {
                // Handle both single text (balloon.text) and multiple texts (balloon.texts)
                const text_nodes_to_process = [];
                
                if (balloon_group.texts && Array.isArray(balloon_group.texts) && balloon_group.texts.length > 0) {
                    // Merged balloon with multiple texts
                    text_nodes_to_process.push(...balloon_group.texts);
                } else if (balloon_group.text) {
                    // Single text balloon
                    text_nodes_to_process.push(balloon_group.text);
                }
                text_nodes_to_process.forEach(original_text_node => {
                    if (!original_text_node) {
                        console.warn("Balloon is missing a text node:", balloon_group.id());
                        return;
                    }

                    const cloned_text_node = original_text_node.clone();
                    cloned_text_node.draggable(false);

                    let original_box_spec;
                    
                    // Check if this text node has its own custom box (from previous merges)
                    const text_custom_box = original_text_node.getAttr('custom_box_for_recenter');
                    if (text_custom_box && typeof text_custom_box === 'object') {
                        // Use the text node's own custom box
                        original_box_spec = {
                            x: text_custom_box.x,
                            y: text_custom_box.y,
                            width: text_custom_box.width,
                            height: text_custom_box.height
                        };
                    } else {
                        // Check if the group has custom position settings
                        const custom_box_defined_on_group = balloon_group.getAttr('custom_text_position') === true &&
                                               typeof balloon_group.getAttr('custom_text_box_width') === 'number' &&
                                               balloon_group.getAttr('custom_text_box_width') > 0;

                        if (custom_box_defined_on_group) {
                            original_box_spec = {
                                x: balloon_group.getAttr('custom_text_box_x'),
                                y: balloon_group.getAttr('custom_text_box_y'),
                                width: balloon_group.getAttr('custom_text_box_width'),
                                height: balloon_group.getAttr('custom_text_box_height')
                            };
                        } 
                        else {
                            // Use the balloon shape bounds as the text box (preserve original balloon dimensions)
                            const shape_rect_relative_to_group = balloon_group.shape.getClientRect({ relativeTo: balloon_group });
                            original_box_spec = {
                                x: shape_rect_relative_to_group.x,
                                y: shape_rect_relative_to_group.y,
                                width: shape_rect_relative_to_group.width,
                                height: shape_rect_relative_to_group.height
                            };
                        }
                    }

                    text_data_for_merge.push({
                        cloned_text_node: cloned_text_node,
                        original_group_abs_transform: balloon_group.getAbsoluteTransform().copy(),
                        original_text_abs_pos: original_text_node.getAbsolutePosition(),
                        original_box_spec: original_box_spec
                    });
                });
            });
            
            if (text_data_for_merge.length === 0) {
                console.error("No text data found in selected balloons.");
                this.status_message = this.get_t('status.merge_failed_no_text_content');
                return;
            }

            // Pass all balloons to merge_shapes
            this.merge_shapes(balloons_to_merge)
                .then(merge_result => {
                    if (!merge_result) {
                        this.status_message = this.get_t('merge_failed_unexpected_status');
                        console.error("Merge operation returned null or undefined.");
                        return;
                    }

                    const { d_attribute, abs_x, abs_y, error } = merge_result;

                    if (error) {
                        if (error === 'no_intersection') {
                            this.status_message = this.get_t('balloons_do_not_overlap_merge_status');
                            this.show_popup_with_content(
                                this.get_t('merge_error_title'),
                                `<div class="text-center space-y-4">
                                    <div class="text-lg font-medium text-gray-100">${this.get_t('balloons_do_not_overlap_merge_popup')}</div>
                                </div>
                                <div class="flex justify-center mt-8">
                                    <button id="ok_btn_merge_no_overlap" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                                        ${this.get_t('actions.ok')}
                                    </button>
                                </div>`,
                                { type: 'warning' }
                            );
                            this.$nextTick(() => {
                                const ok_btn = document.getElementById('ok_btn_merge_no_overlap');
                                if (ok_btn) ok_btn.onclick = () => this.close_popup();
                            });
                        } else {
                            this.status_message = this.get_t('merge_failed_details_status', { error });
                            console.error("Merge operation failed with error:", error, "Result:", merge_result);
                        }
                        return;
                    }

                    if (d_attribute) { 
                        this.status_message = this.get_t('status.shapes_merged_creating_balloon');
                        
                        if (!this.selected_panel || !this.selected_panel.content_group) { 
                            console.error("No selected panel to add the merged shape to (consistency check).");
                            this.status_message = this.get_t('status.merge_successful_no_panel_selected');
                            return;
                        }
                        const new_balloon_points = this._convert_path_data_to_balloon_points(d_attribute);
                        if (!new_balloon_points || new_balloon_points.length === 0) {
                            console.error("Failed to convert merged path to balloon points.");
                            this.status_message = this.get_t('merge_failed_points_conversion_status');
                            return;
                        }

                        // Determine final style properties from all balloons
                        const style_properties = this._determine_merged_style_properties(balloons_to_merge);

                        const merged_shape = new Konva.Path({
                            data: d_attribute,
                            fill: style_properties.final_fill_applied,
                            stroke: style_properties.final_stroke,
                            strokeWidth: style_properties.final_stroke_width,
                            name: 'balloon_shape',
                            x: 0,
                            y: 0
                        });

                        const parent_transform_inverse = parent_group_for_merge.getAbsoluteTransform().copy().invert();
                        const relative_pos_group = parent_transform_inverse.point({ x: abs_x, y: abs_y });

                        const merged_balloon_group_raw = new Konva.Group({
                            x: relative_pos_group.x,
                            y: relative_pos_group.y,
                            draggable: true,
                            name: 'panel_balloon',
                            id: 'balloon_' + Date.now(),
                        });

                        const merged_balloon_group = markRaw(merged_balloon_group_raw);

                        merged_balloon_group.add(merged_shape);
                        merged_balloon_group.shape = merged_shape;
                        
                        // Set default blend mode and opacity for proper layer controls
                        merged_balloon_group.globalCompositeOperation('normal');
                        merged_balloon_group.opacity(1);
                        
                        // Add to parent NOW so its absolute transform is correctly calculated for the next step
                        parent_group_for_merge.add(merged_balloon_group);
                        
                        const merged_group_inv_abs_transform = merged_balloon_group.getAbsoluteTransform().copy().invert();
                        const processed_texts = [];

                        text_data_for_merge.forEach(data => {
                            const cloned_text = data.cloned_text_node;

                            // Calculate the new relative position for the text node
                            const new_relative_text_pos = merged_group_inv_abs_transform.point(data.original_text_abs_pos);
                            cloned_text.position(new_relative_text_pos);

                            // Calculate and set custom_box_for_recenter
                            const abs_box_top_left = data.original_group_abs_transform.point({
                                x: data.original_box_spec.x,
                                y: data.original_box_spec.y
                            });

                            const new_relative_box_top_left = merged_group_inv_abs_transform.point(abs_box_top_left);

                            cloned_text.setAttr('custom_box_for_recenter', {
                                x: new_relative_box_top_left.x,
                                y: new_relative_box_top_left.y,
                                width: data.original_box_spec.width, 
                                height: data.original_box_spec.height
                            });

                            merged_balloon_group.add(cloned_text);
                            processed_texts.push(cloned_text);
                        });
                        
                        merged_balloon_group.texts = processed_texts;
                        merged_balloon_group.text = null;
                        merged_balloon_group.setAttr('object_type', 'balloon');
                        merged_balloon_group.setAttr('balloon_points', new_balloon_points);
                        merged_balloon_group.setAttr('original_stroke_width', style_properties.final_stroke_width);
                        merged_balloon_group.setAttr('original_fill', style_properties.final_fill_raw);
                        merged_balloon_group.setAttr('original_stroke', style_properties.final_stroke);
                        merged_balloon_group.setAttr('fill_opacity', style_properties.final_opacity);
                        // Store original merged path data to preserve complex shapes with holes
                        merged_balloon_group.setAttr('merged_path_data', d_attribute);

                        const relative_bounds_for_attrs = merged_shape.getClientRect({relativeTo: merged_balloon_group});
                        merged_balloon_group.setAttr('balloon_width', relative_bounds_for_attrs.width);
                        merged_balloon_group.setAttr('balloon_height', relative_bounds_for_attrs.height);
                        merged_balloon_group.setAttr('balloon_cx', relative_bounds_for_attrs.x + relative_bounds_for_attrs.width / 2);
                        merged_balloon_group.setAttr('balloon_cy', relative_bounds_for_attrs.y + relative_bounds_for_attrs.height / 2);

                        merged_balloon_group.on('transform', (e) => {
                            const group = e.target;
                            this.recenter_balloon_text(group);
                        });

                        // Destroy all original balloons
                        balloons_to_merge.forEach(balloon => {
                            balloon.destroy();
                        });

                        this.transformer.nodes([]);
                        this.transformer.detach();

                        this.select_object(merged_balloon_group);

                        this.panels_layer.batchDraw();
                        this.push_to_history('merge_balloons');

                        this.recenter_balloon_text(merged_balloon_group);
                        this.panels_layer.batchDraw();

                        this.status_message = this.get_t('balloons_merged_successfully_status');

                    } else {
                        this.status_message = this.get_t('merge_failed_no_data_status');
                        console.error("Merge operation failed to return valid data. Result:", merge_result);
                    }
                })
                .catch(error => {
                    console.error("Error in handle_merge_selected_balloons promise:", error);
                    this.status_message = this.get_t('merge_failed_exception_status');
                });
        },

        // Helper function to determine merged style properties from multiple balloons
        _determine_merged_style_properties(balloons_to_merge) {
            if (balloons_to_merge.length === 0) {
                return {
                    final_fill_raw: this.balloon_default_fill,
                    final_fill_applied: this.balloon_default_fill,
                    final_stroke: this.balloon_default_stroke,
                    final_stroke_width: this.balloon_default_stroke_width,
                    final_opacity: this.preferences.balloon_defaults.alpha
                };
            }

            // Get properties from all balloons
            const fills = balloons_to_merge.map(balloon => 
                balloon.getAttr('original_fill') || balloon.shape.fill()
            );
            const strokes = balloons_to_merge.map(balloon => 
                balloon.getAttr('original_stroke') || balloon.shape.stroke()
            );
            const stroke_widths = balloons_to_merge.map(balloon => 
                balloon.getAttr('original_stroke_width') || balloon.shape.strokeWidth()
            );
            const opacities = balloons_to_merge.map(balloon => {
                const opacity = balloon.getAttr('fill_opacity');
                if (opacity !== undefined) return opacity;
                const shape_opacity = balloon.shape.opacity();
                return shape_opacity !== undefined ? shape_opacity : this.preferences.balloon_defaults.alpha;
            });

            // Use most common values, or defaults if all different
            const final_fill_raw = this._get_most_common_value(fills) || this.balloon_default_fill;
            const final_stroke = this._get_most_common_value(strokes) || this.balloon_default_stroke;
            const final_stroke_width = this._get_most_common_value(stroke_widths) || this.balloon_default_stroke_width;
            const final_opacity = this._get_most_common_value(opacities) || this.preferences.balloon_defaults.alpha;

            // Apply opacity to fill color
            let final_fill_applied = final_fill_raw;
            if (final_opacity < 1 && typeof final_fill_raw === 'string') {
                if (final_fill_raw.startsWith('#')) {
                    const r = parseInt(final_fill_raw.slice(1, 3), 16);
                    const g = parseInt(final_fill_raw.slice(3, 5), 16);
                    const b = parseInt(final_fill_raw.slice(5, 7), 16);
                    final_fill_applied = `rgba(${r},${g},${b},${final_opacity})`;
                } else if (final_fill_raw.startsWith('rgb(')) {
                     const match = final_fill_raw.match(/rgb\((\d+),\s*(\d+),\s*(\d+)/);
                     if (match) {
                        final_fill_applied = `rgba(${match[1]},${match[2]},${match[3]},${final_opacity})`;
                     }
                }
            }

            return {
                final_fill_raw,
                final_fill_applied,
                final_stroke,
                final_stroke_width,
                final_opacity
            };
        },

        // Helper function to get the most common value from an array
        _get_most_common_value(values) {
            if (!values || values.length === 0) return null;
            
            const frequency = {};
            let max_count = 0;
            let most_common = values[0];

            values.forEach(value => {
                const count = (frequency[value] || 0) + 1;
                frequency[value] = count;
                if (count > max_count) {
                    max_count = count;
                    most_common = value;
                }
            });

            return most_common;
        },

        /**
         * Adds a point to balloon and recalculates all nodes and handlers
         */
        add_point_to_balloon(balloon, point, options = {}) {
            if (!balloon || !point) return;

            const preserve_node_properties = options.preserve_node_properties || false;
            const no_adjust_to_border = options.no_adjust_to_border || false;
            const force_insert_at = typeof options.force_insert_at === 'number' ? options.force_insert_at : null;

            let pts = balloon.getAttr('balloon_points') || [];
            const main_pts = pts.filter(p => p.type === 'main');
            if (main_pts.length < 2) {
                console.warn("Need at least 2 main points to add a node.");
                return;
            }

            let insert_at_index = null;
            let new_node = null;

            if (force_insert_at !== null) {
                // Insert at explicit index, do not adjust position or control points
                insert_at_index = force_insert_at;
                new_node = { ...point };
            } else {
                // Find the closest Bezier segment and the parameter 't'
                let closest_segment_info = null;
                let min_dist_sq = Infinity;
                for (let i = 0; i < main_pts.length; i++) {
                    const p0_node = main_pts[i];
                    const p3_node = main_pts[(i + 1) % main_pts.length];
                    const p1 = { x: p0_node.cp_out_x ?? p0_node.x, y: p0_node.cp_out_y ?? p0_node.y };
                    const p2 = { x: p3_node.cp_in_x ?? p3_node.x, y: p3_node.cp_in_y ?? p3_node.y };
                    const steps = 20;
                    for (let j = 0; j <= steps; j++) {
                        const t = j / steps;
                        const curve_point = this.get_bezier_point(t, p0_node, p1, p2, p3_node);
                        const dist_sq = (point.x - curve_point.x)**2 + (point.y - curve_point.y)**2;
                        if (dist_sq < min_dist_sq) {
                            min_dist_sq = dist_sq;
                            closest_segment_info = {
                                p0: p0_node, p1: p1, p2: p2, p3: p3_node,
                                p0_idx: pts.findIndex(p => p === p0_node),
                                p3_idx: pts.findIndex(p => p === p3_node),
                                t: t
                            };
                        }
                    }
                }
                if (!closest_segment_info) {
                    console.error("Could not find closest segment.");
                    return;
                }

                // If not preserving node properties, adjust to border and recalc control points
                if (!preserve_node_properties && !no_adjust_to_border) {
                    const P = this.get_bezier_point(closest_segment_info.t,
                        closest_segment_info.p0,
                        closest_segment_info.p1,
                        closest_segment_info.p2,
                        closest_segment_info.p3);

                    const subdivided_segments = this.subdivide_bezier(
                        closest_segment_info.p0,
                        closest_segment_info.p1,
                        closest_segment_info.p2,
                        closest_segment_info.p3,
                        closest_segment_info.t
                    );

                    new_node = {
                        x: P.x, y: P.y, type: 'main', node_type: 'smooth',
                        tangency_broken: false,
                        cp_in_x: subdivided_segments.q2.x,
                        cp_in_y: subdivided_segments.q2.y,
                        cp_out_x: subdivided_segments.r1.x,
                        cp_out_y: subdivided_segments.r1.y,
                    };

                    // Update the control points of the neighboring original nodes
                    const p0_node = pts[closest_segment_info.p0_idx];
                    const p3_node = pts[closest_segment_info.p3_idx];
                    p0_node.cp_out_x = subdivided_segments.q1.x;
                    p0_node.cp_out_y = subdivided_segments.q1.y;
                    p3_node.cp_in_x = subdivided_segments.r2.x;
                    p3_node.cp_in_y = subdivided_segments.r2.y;
                } else {
                    // If preserving node properties or not adjusting to border, use the point as is
                    new_node = { ...point };
                }
                insert_at_index = closest_segment_info.p0_idx + 1;
                if (closest_segment_info.p0_idx === pts.length - 1 && closest_segment_info.p3_idx === 0) {
                    insert_at_index = pts.length;
                }
            }

            pts.splice(insert_at_index, 0, new_node);

            balloon.setAttr('balloon_points', pts);
            balloon.shape.data(this.generate_balloon_path_from_points(pts));

            if (balloon.getAttr('editing_mode')) {
                this.remove_balloon_edit_handles(balloon);
                this.create_balloon_edit_handles(balloon);
            }

            balloon.getLayer()?.batchDraw();
        },

        /**
         * Naturalizes the balloon shape by adding nodes and randomly displacing them (except pointer nodes)
         * @param {Object} balloon - The Konva.Group balloon object
         */
        naturalize_balloon(balloon, seed, set_history = true) {
            // Hardcoded parameters for node count and intensity
            let nodes_to_add = 10;
            let displacement_intensity = 8; // pixels
            let smooth_ratio = 0.5; // Ratio of smooth nodes (rest will be corner)

            const rand = this.seeded_random_factory(seed);

            //randomizer
            nodes_to_add = 9 + rand() * 10; // 9 + [0-1] * 10 = [9, 19]
            displacement_intensity = 6 + rand() * 2; // 6 + [0-1] * 2 = [6, 8]
            smooth_ratio = 0.5 + rand() * 0.4; // 0.5 + [0-1] * 0.4 = [0.5, 0.9]

            // Get current points
            let pts = balloon.getAttr('balloon_points') || [];
            if (pts.length < 2) return;

            // Helper: check if a node is non-naturalizable
            const is_non_naturalizable = (pt) => pt.is_non_naturalizable === true;

            // Step 1: Add nodes at regular intervals (skip if already dense)
            let main_pts = pts.filter(p => p.type === 'main');
            if (main_pts.length < nodes_to_add) {
                const to_add_per_segment = Math.floor(nodes_to_add / main_pts.length);
                for (let i = 0; i < main_pts.length; i++) {
                    const p0 = main_pts[i];
                    const p1 = main_pts[(i + 1) % main_pts.length];
                    if (is_non_naturalizable(p0) && is_non_naturalizable(p1)) continue;
                    for (let j = 1; j <= to_add_per_segment; j++) {
                        const t = j / (to_add_per_segment + 1);
                        const new_x = p0.x + (p1.x - p0.x) * t;
                        const new_y = p0.y + (p1.y - p0.y) * t;

                        this.add_point_to_balloon(balloon, { 
                            x: new_x, 
                            y: new_y, 
                            type: 'main', 
                            node_type: 'smooth',
                            tangency_broken: true 
                        });
                    }
                }
                pts = balloon.getAttr('balloon_points') || [];
            }

            // Step 2: Randomly displace nodes (skip non-naturalizable)
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (is_non_naturalizable(pt)) continue;
                // Use seeded random for angle and radius
                const angle = rand() * 2 * Math.PI;
                const radius = (rand() * 0.5 + 0.5) * displacement_intensity;
                pt.x += Math.cos(angle) * radius;
                pt.y += Math.sin(angle) * radius;
            }
            
            main_pts = pts.filter(p => p.type === 'main');
            // First pass: Add nodes
            if (main_pts.length < nodes_to_add) {
                const to_add_per_segment = Math.floor(nodes_to_add / main_pts.length);
                for (let i = 0; i < main_pts.length; i++) {
                    const p0 = main_pts[i];
                    const p1 = main_pts[(i + 1) % main_pts.length];
                    if (is_non_naturalizable(p0) && is_non_naturalizable(p1)) continue;
                    for (let j = 1; j <= to_add_per_segment; j++) {
                        const t = j / (to_add_per_segment + 1);
                        const new_x = p0.x + (p1.x - p0.x) * t;
                        const new_y = p0.y + (p1.y - p0.y) * t;
                        const node_type = (rand() < smooth_ratio) ? 'smooth' : 'corner';
                        // Always set type and node_type
                        this.add_point_to_balloon(balloon, { x: new_x, y: new_y, type: 'main', node_type: node_type });
                    }
                }
                pts = balloon.getAttr('balloon_points') || [];
            }

            // Second pass: Displace nodes
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (is_non_naturalizable(pt)) continue;
                // Use seeded random for angle and radius
                const angle = rand() * 2 * Math.PI;
                const radius = (rand() * 0.5 + 0.5) * displacement_intensity;
                pt.x += Math.cos(angle) * radius;
                pt.y += Math.sin(angle) * radius;
            }
            
            pts = balloon.getAttr('balloon_points') || [];
            // Get updated main points
            main_pts = pts.filter(p => p.type === 'main');
            
            // Third pass: Analyze geometry and correct node types based on actual path geometry
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (pt.type !== 'main') continue;
                
                // Find neighbors (main points)
                let prev = null, next = null;
                for (let j = 1; j < pts.length; j++) {
                    const idx_prev = (i - j + pts.length) % pts.length;
                    if (pts[idx_prev].type === 'main') { prev = pts[idx_prev]; break; }
                }
                for (let j = 1; j < pts.length; j++) {
                    const idx_next = (i + j) % pts.length;
                    if (pts[idx_next].type === 'main') { next = pts[idx_next]; break; }
                }
                
                if (!prev || !next) continue;
                
                // Use the same logic from _convert_path_data_to_balloon_points for node type determination
                const epsilon = 0.001;
                
                // Default to smooth with broken tangency
                pt.node_type = 'smooth';
                pt.tangency_broken = true;
                
                // Check if control points are at the anchor point (indicating corner)
                const is_cp_in_at_anchor = Math.abs(pt.cp_in_x - pt.x) < epsilon && Math.abs(pt.cp_in_y - pt.y) < epsilon;
                const is_cp_out_at_anchor = Math.abs(pt.cp_out_x - pt.x) < epsilon && Math.abs(pt.cp_out_y - pt.y) < epsilon;
                
                if (is_cp_in_at_anchor && is_cp_out_at_anchor) {
                    // If both control points are at the anchor point, it's a corner
                    pt.node_type = 'corner';
                    pt.tangency_broken = true;
                } else {
                    // It's a smooth node, check tangency
                    pt.node_type = 'smooth';
                    
                    if (is_cp_in_at_anchor || is_cp_out_at_anchor) {
                        // If either control point is at anchor, tangency is broken
                        pt.tangency_broken = true;
                    } else {
                        // Check if control points are collinear and point in opposite directions
                        const v_in_x = pt.x - pt.cp_in_x; // Vector from cp_in to anchor
                        const v_in_y = pt.y - pt.cp_in_y;
                        const v_out_x = pt.cp_out_x - pt.x; // Vector from anchor to cp_out
                        const v_out_y = pt.cp_out_y - pt.y;
                        
                        const len_v_in_sq = v_in_x * v_in_x + v_in_y * v_in_y;
                        const len_v_out_sq = v_out_x * v_out_x + v_out_y * v_out_y;
                        
                        if (len_v_in_sq < epsilon * epsilon || len_v_out_sq < epsilon * epsilon) {
                            // If either vector is too short, tangency is broken
                            pt.tangency_broken = true;
                        } else {
                            // Calculate dot product to check alignment
                            const dot_product = v_in_x * v_out_x + v_in_y * v_out_y;
                            const cos_theta = dot_product / (Math.sqrt(len_v_in_sq) * Math.sqrt(len_v_out_sq));
                            
                            // For unbroken tangency, vectors should point in opposite directions
                            // (cos_theta close to -1)
                            if (cos_theta < -0.99) {
                                pt.tangency_broken = false;
                            } else {
                                pt.tangency_broken = true;
                            }
                        }
                    }
                }
            }
            
            // Final step: Ensure all nodes have correct type based on their geometry
            const epsilon = 0.001;
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (pt.type !== 'main') continue;
                
                // Use the same logic from _convert_path_data_to_balloon_points for consistent node type determination
                const is_cp_in_at_anchor = Math.abs(pt.cp_in_x - pt.x) < epsilon && Math.abs(pt.cp_in_y - pt.y) < epsilon;
                const is_cp_out_at_anchor = Math.abs(pt.cp_out_x - pt.x) < epsilon && Math.abs(pt.cp_out_y - pt.y) < epsilon;
                
                if (is_cp_in_at_anchor && is_cp_out_at_anchor) {
                    // Both control points at anchor = corner node
                    pt.node_type = 'corner';
                    pt.tangency_broken = true;
                } else {
                    // It's a smooth node
                    pt.node_type = 'smooth';
                    
                    if (is_cp_in_at_anchor || is_cp_out_at_anchor) {
                        // If either control point is at anchor, tangency is broken
                        pt.tangency_broken = true;
                    } else {
                        // Check if control points are collinear and point in opposite directions
                        const v_in_x = pt.x - pt.cp_in_x;
                        const v_in_y = pt.y - pt.cp_in_y;
                        const v_out_x = pt.cp_out_x - pt.x;
                        const v_out_y = pt.cp_out_y - pt.y;
                        
                        const len_v_in_sq = v_in_x * v_in_x + v_in_y * v_in_y;
                        const len_v_out_sq = v_out_x * v_out_x + v_out_y * v_out_y;
                        
                        if (len_v_in_sq < epsilon * epsilon || len_v_out_sq < epsilon * epsilon) {
                            // If either vector is too small, tangency is broken
                            pt.tangency_broken = true;
                        } else {
                            // Calculate dot product to check alignment
                            const dot_product = v_in_x * v_out_x + v_in_y * v_out_y;
                            const cos_theta = dot_product / (Math.sqrt(len_v_in_sq) * Math.sqrt(len_v_out_sq));
                            
                            // For unbroken tangency, vectors should point in opposite directions
                            pt.tangency_broken = cos_theta > -0.99;
                        }
                    }
                }
            }
            
            balloon.setAttr('balloon_points', pts);
            balloon.shape.data(this.generate_balloon_path_from_points(pts));
            balloon.getLayer()?.batchDraw();
            //this.show_transformer(balloon); //update transformer after shape change
            // Register action in history
            if (set_history == true){
                this.push_to_history('naturalize_balloon');
            }            
        },


         /**
         * Simplifies and smooths the balloon shape by removing unnecessary nodes and softening sharp corners.
         * @param {Object} balloon - The Konva.Group balloon object
         */
        simplify_and_smooth_balloon(balloon, seed, set_history = true, low_intensity = false) {
            // Asegurarse de que la semilla se use correctamente
            const rand = this.seeded_random_factory(seed !== undefined ? seed : Date.now());
            
            // Parámetros más conservadores para la simplificación
            let angle_threshold_deg = 160; // Mucho más conservador (era 160)
            let max_distance = 4;      // Reducido (era 3)
            let flat_threshold = 5;    // Reducido (era 5)
            let min_nodes = 6;       // Numero mínimo de nodos a mantener
            let sharp_angle_deg = 120; // Ángulos agudos a suavizar (era 90)
            let handle_factor = 0.25; 

            if (low_intensity) {
                sharp_angle_deg = 150; 
                flat_threshold = 3;
                max_distance = 6;
                handle_factor = 0.14;

                //console.log("sharp_angle_deg:", sharp_angle_deg, "flat_threshold:", flat_threshold, "max_distance:", max_distance);
            } else {
                // Modo normal: valores aleatorios
                flat_threshold = 3 + rand() * 4; // 3 + [0-1] * 4 = range [3-7]
                max_distance = 3 + rand() * 3; // 3 + [0-1] * 3 = range [3-6]
                handle_factor = 0.14 + rand() * 0.15; // 0.14 + [0-1] * 0.15 = range [0.14-0.29]
            }

            let pts = balloon.getAttr('balloon_points') || [];
            if (pts.length < 3) return;

            // Helper: check if a node is non-naturalizable
            const is_non_naturalizable = (pt) => pt.is_non_naturalizable === true;
            
            // Contar el número de nodos principales antes de simplificar
            let main_pts = pts.filter(p => p.type === 'main');
            const original_node_count = main_pts.length;
            
            // Solo aplicar simplificación si hay suficientes nodos
            let to_remove = [];
            if (original_node_count > min_nodes) {
                // Step 1: Solo eliminar nodos realmente redundantes (casi duplicados)
                for (let i = 0; i < main_pts.length; i++) {
                    const curr = main_pts[i];
                    if (is_non_naturalizable(curr)) continue;
                    
                    const prev = main_pts[(i - 1 + main_pts.length) % main_pts.length];
                    const next = main_pts[(i + 1) % main_pts.length];
                    
                    // Distancia muy pequeña al vecino (duplicados)
                    const dist_prev = Math.hypot(curr.x - prev.x, curr.y - prev.y);
                    if (dist_prev < 2 && !is_non_naturalizable(prev)) { 
                        to_remove.push(curr);
                        continue;
                    }
                    
                    // Step 2: Solo eliminar nodos que sean colineales con sus vecinos 
                    // y que afecten poco la forma, usando la seed para variación
                    const v1x = curr.x - prev.x;
                    const v1y = curr.y - prev.y;
                    const v2x = next.x - curr.x;
                    const v2y = next.y - curr.y;
                    const len1 = Math.hypot(v1x, v1y);
                    const len2 = Math.hypot(v2x, v2y);
                    
                    if (len1 < 1e-3 || len2 < 1e-3) continue;
                    
                    // Calcular el ángulo entre segmentos
                    const dot = v1x * v2x + v1y * v2y;
                    const angle = Math.acos(Math.max(-1, Math.min(1, dot / (len1 * len2))));
                    const angle_deg = angle * 180 / Math.PI;
                    
                    // Calcular distancia del punto a la línea formada por prev-next
                    const num = Math.abs((next.y - prev.y) * curr.x - (next.x - prev.x) * curr.y + next.x * prev.y - next.y * prev.x);
                    const den = Math.hypot(next.y - prev.y, next.x - prev.x);
                    const dist_to_line = den > 0 ? num / den : 0;
                                        
                    if (angle_deg > angle_threshold_deg && 
                        dist_to_line < max_distance && 
                        rand() > 0.8 && // 20% de probabilidad basada en seed
                        main_pts.length - to_remove.length > min_nodes) { 
                        to_remove.push(curr);
                    }
                    else if (dist_to_line < flat_threshold && 
                             rand() > 0.6 && // 30% de probabilidad basada en seed
                             main_pts.length - to_remove.length > min_nodes) {
                        to_remove.push(curr);
                    }
                }
            }
            
            // Eliminar los nodos marcados
            if (to_remove.length > 0) {
                pts = pts.filter(p => !to_remove.includes(p));
            }
            // Step 3: Suavizar esquinas agudas
            
            
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (pt.type !== 'main' || is_non_naturalizable(pt)) continue;
                
                // Encontrar vecinos
                let prev = null, next = null;
                for (let j = 1; j < pts.length; j++) {
                    const idx_prev = (i - j + pts.length) % pts.length;
                    if (pts[idx_prev].type === 'main') { prev = pts[idx_prev]; break; }
                }
                for (let j = 1; j < pts.length; j++) {
                    const idx_next = (i + j) % pts.length;
                    if (pts[idx_next].type === 'main') { next = pts[idx_next]; break; }
                }
                if (!prev || !next) continue;
                
                // Calcular vectores y ángulo
                const v1x = pt.x - prev.x;
                const v1y = pt.y - prev.y;
                const v2x = next.x - pt.x;
                const v2y = next.y - pt.y;
                const len1 = Math.hypot(v1x, v1y);
                const len2 = Math.hypot(v2x, v2y);
                if (len1 < 1e-3 || len2 < 1e-3) continue;
                
                const dot = v1x * v2x + v1y * v2y;
                const angle = Math.acos(Math.max(-1, Math.min(1, dot / (len1 * len2))));
                const angle_deg = angle * 180 / Math.PI;

                let is_polygon = false;
                
                // Contador de nodos que son esquinas (corners reales, sin curvas)
                let corner_count = 0;
                for (let j = 0; j < main_pts.length; j++) {
                    const node = main_pts[j];
                    // Un nodo es una esquina real si ambos puntos de control coinciden con el punto de anclaje
                    if (Math.abs(node.cp_in_x - node.x) < 1e-3 && 
                        Math.abs(node.cp_in_y - node.y) < 1e-3 && 
                        Math.abs(node.cp_out_x - node.x) < 1e-3 && 
                        Math.abs(node.cp_out_y - node.y) < 1e-3) {
                        corner_count++;
                    }
                }
                
                // Es un polígono si la mayoría de los nodos son esquinas y tiene pocos nodos
                is_polygon = (corner_count >= main_pts.length * 0.7) && main_pts.length <= 7;
                
                // Suavizar esquinas en polígonos o solo las esquinas agudas en formas curvas
                if (is_polygon || 
                    (!is_polygon && (pt.node_type === 'corner' || (pt.node_type === 'smooth' && pt.tangency_broken)) && 
                     angle_deg < sharp_angle_deg)) {

                    if (is_polygon) {
                        handle_factor = 0.05;
                    }
                    
                    pt.node_type = 'smooth';
                    pt.tangency_broken = false;
                    
                    const cp_in_dist = len1 * handle_factor;
                    const cp_out_dist = len2 * handle_factor;
                    const tangent_angle = Math.atan2(next.y - prev.y, next.x - prev.x);
                    
                    pt.cp_in_x = pt.x - Math.cos(tangent_angle) * cp_in_dist;
                    pt.cp_in_y = pt.y - Math.sin(tangent_angle) * cp_in_dist;
                    pt.cp_out_x = pt.x + Math.cos(tangent_angle) * cp_out_dist;
                    pt.cp_out_y = pt.y + Math.sin(tangent_angle) * cp_out_dist;
                }
            }
            
            // Final step: Ensure all nodes have correct type based on their geometry
            const epsilon = 0.001;
            for (let i = 0; i < pts.length; i++) {
                const pt = pts[i];
                if (pt.type !== 'main') continue;
                
                // Use the same logic from _convert_path_data_to_balloon_points for consistent node type determination
                const is_cp_in_at_anchor = Math.abs(pt.cp_in_x - pt.x) < epsilon && Math.abs(pt.cp_in_y - pt.y) < epsilon;
                const is_cp_out_at_anchor = Math.abs(pt.cp_out_x - pt.x) < epsilon && Math.abs(pt.cp_out_y - pt.y) < epsilon;
                
                if (is_cp_in_at_anchor && is_cp_out_at_anchor) {
                    // Both control points at anchor = corner node
                    pt.node_type = 'corner';
                    pt.tangency_broken = true;
                } else {
                    // It's a smooth node
                    pt.node_type = 'smooth';
                    
                    if (is_cp_in_at_anchor || is_cp_out_at_anchor) {
                        // If either control point is at anchor, tangency is broken
                        pt.tangency_broken = true;
                    } else {
                        // Check if control points are collinear and point in opposite directions
                        const v_in_x = pt.x - pt.cp_in_x;
                        const v_in_y = pt.y - pt.cp_in_y;
                        const v_out_x = pt.cp_out_x - pt.x;
                        const v_out_y = pt.cp_out_y - pt.y;
                        
                        const len_v_in_sq = v_in_x * v_in_x + v_in_y * v_in_y;
                        const len_v_out_sq = v_out_x * v_out_x + v_out_y * v_out_y;
                        
                        if (len_v_in_sq < epsilon * epsilon || len_v_out_sq < epsilon * epsilon) {
                            // If either vector is too small, tangency is broken
                            pt.tangency_broken = true;
                        } else {
                            // Calculate dot product to check alignment
                            const dot_product = v_in_x * v_out_x + v_in_y * v_out_y;
                            const cos_theta = dot_product / (Math.sqrt(len_v_in_sq) * Math.sqrt(len_v_out_sq));
                            
                            // For unbroken tangency, vectors should point in opposite directions
                            pt.tangency_broken = cos_theta > -0.99;
                        }
                    }
                }
            }
            
            balloon.setAttr('balloon_points', pts);
            balloon.shape.data(this.generate_balloon_path_from_points(pts));
            balloon.getLayer()?.batchDraw();
            //this.show_transformer(balloon); //update transformer after shape change
            // Register action in history
            if (set_history == true){
                this.push_to_history('simplify_and_smooth_balloon');
            }
        },
        
        randomize_balloon(balloon, seed) {
            // Capture original balloon dimensions BEFORE any changes (not transformer)
            var original_balloon_bounds = balloon.getClientRect();
            var original_width = original_balloon_bounds.width;
            var original_height = original_balloon_bounds.height;  

            this.change_balloon_type(balloon, 'speech', true, false);

            this.naturalize_balloon(balloon, seed, false);
            this.simplify_and_smooth_balloon(balloon, seed, false, true);    

            // Apply distortion to make the balloon fit exactly in the original balloon area
            this.distort_balloon_to_fit(balloon, original_width, original_height);

            this.push_to_history('randomize_balloon');
        },
          
        /**
         * Distorts a balloon shape to fit exactly within specified dimensions
         * by modifying the balloon points directly (like bake_balloon_transform)
         * @param {Konva.Group} balloon - The balloon group to distort
         * @param {number} target_width - Target width to fit into
         * @param {number} target_height - Target height to fit into
         */
        distort_balloon_to_fit(balloon, target_width, target_height) {
            if (!balloon || !balloon.shape) return;

            // Get current balloon group bounds
            const balloon_bounds = balloon.getClientRect();
            
            // Calculate scale factors needed
            const scale_x = target_width / balloon_bounds.width;
            const scale_y = target_height / balloon_bounds.height;
                        
            // Modify balloon points directly (like bake_balloon_transform does)
            const points = balloon.getAttr('balloon_points');
            if (points) {
                points.forEach(point => {
                    point.x *= scale_x;
                    point.y *= scale_y;
                    if (point.cp_in_x !== undefined) point.cp_in_x *= scale_x;
                    if (point.cp_in_y !== undefined) point.cp_in_y *= scale_y;
                    if (point.cp_out_x !== undefined) point.cp_out_x *= scale_x;
                    if (point.cp_out_y !== undefined) point.cp_out_y *= scale_y;
                });
                
                // Update the balloon points attribute
                balloon.setAttr('balloon_points', points);
                
                // Regenerate the path from modified points
                const new_path_data = this.generate_balloon_path_from_points(points);
                balloon.shape.data(new_path_data);
            }
            
            // Scale text positions accordingly
            if (balloon.texts && Array.isArray(balloon.texts)) {
                balloon.texts.forEach(text_node => {
                    text_node.x(text_node.x() * scale_x);
                    text_node.y(text_node.y() * scale_y);
                });
            } else if (balloon.text) {
                balloon.text.x(balloon.text.x() * scale_x);
                balloon.text.y(balloon.text.y() * scale_y);
            }
            
            
            // Re-center the text after distortion
            this.recenter_balloon_text(balloon);
        },

        /**
         * Generates adaptive thought balloon points based on balloon dimensions
         * @param {number} width - Balloon width
         * @param {number} height - Balloon height
         * @returns {Array} Array of balloon points
         */
        generate_adaptive_thought_balloon_points(width, height) {
            const hw = width / 2;
            const hh = height / 2;
    
            // Determine effective simplicity: for small balloons, always use minimal simplicity
            // For larger balloons, respect user preference
            const is_small_balloon = Math.min(width, height) < 180;
            const effective_simplicity = is_small_balloon ? 0 : this.thought_balloon_simplicity;
            
            // Calculate bubble size based on simplicity slider (0-1)
            // 0 = complex (80 base, 10 variation), 1 = simple (120 base, 30 variation)
            const base_bubble_width = 80 + (effective_simplicity * 40);  // 80 to 120
            const variation_range = 10 + (effective_simplicity * 20);    // 10 to 30
            
            const seed = Date.now() % 1000;
            const rand = this.seeded_random_factory(seed);
            
            // CONSTANT mountain height - same perpendicular distance for all bumps
            const constant_mountain_height = 15;
            
            // Calculate total perimeter by sampling many points
            const perimeter_samples = 400;
            let total_perimeter = 0;
            const perimeter_points = [];
            
            for (let i = 0; i < perimeter_samples; i++) {
                const angle = (i / perimeter_samples) * Math.PI * 2;
                const x = Math.cos(angle) * hw;
                const y = Math.sin(angle) * hh;
                perimeter_points.push({ angle, x, y });
                
                if (i > 0) {
                    const prev = perimeter_points[i - 1];
                    const dx = x - prev.x;
                    const dy = y - prev.y;
                    total_perimeter += Math.sqrt(dx * dx + dy * dy);
                }
            }
            
            // Close the loop
            const first = perimeter_points[0];
            const last = perimeter_points[perimeter_samples - 1];
            const dx = first.x - last.x;
            const dy = first.y - last.y;
            total_perimeter += Math.sqrt(dx * dx + dy * dy);
            // Generate bubble positions along perimeter
            const bubble_positions = [];
            let current_distance = 0;
              while (current_distance < total_perimeter) {
                const bubble_width = base_bubble_width + (rand() - 0.5) * variation_range * 2;
                
                if (current_distance + bubble_width > total_perimeter) {
                    // Check if we can add one more bubble closing to the start
                    const remaining_distance = total_perimeter - current_distance;
                    
                    // Only add final bubble if there's enough space (at least 75% of normal bubble width)
                    if (remaining_distance > bubble_width * 0.75) {
                        // Add final bubble that connects to the start
                        bubble_positions.push({
                            start: current_distance,
                            width: remaining_distance,
                            center: current_distance + remaining_distance / 2,
                            end: total_perimeter,
                            closes_loop: true
                        });
                    } else {
                        // Not enough space - extend the last bubble to close the loop
                        if (bubble_positions.length > 0) {
                            const last_bubble = bubble_positions[bubble_positions.length - 1];
                            last_bubble.width = total_perimeter - last_bubble.start;
                            last_bubble.center = last_bubble.start + last_bubble.width / 2;
                            last_bubble.end = total_perimeter;
                            last_bubble.closes_loop = true;
                        }
                    }
                    break;
                }
                
                bubble_positions.push({
                    start: current_distance,
                    width: bubble_width,
                    center: current_distance + bubble_width / 2,
                    end: current_distance + bubble_width,
                    closes_loop: false
                });
                
                current_distance += bubble_width;
            }
            
            // Helper function to get position at distance along perimeter
            const get_position_at_distance = (target_distance) => {
                let accumulated = 0;
                
                for (let i = 1; i < perimeter_points.length; i++) {
                    const prev = perimeter_points[i - 1];
                    const curr = perimeter_points[i];
                    const dx = curr.x - prev.x;
                    const dy = curr.y - prev.y;
                    const segment_length = Math.sqrt(dx * dx + dy * dy);
                    
                    if (accumulated + segment_length >= target_distance) {
                        const ratio = (target_distance - accumulated) / segment_length;
                        return {
                            x: prev.x + (curr.x - prev.x) * ratio,
                            y: prev.y + (curr.y - prev.y) * ratio,
                            angle: prev.angle + (curr.angle - prev.angle) * ratio
                        };
                    }
                    accumulated += segment_length;
                }
                
                // Handle wrap-around for the last segment
                const ratio = (target_distance - accumulated) / Math.sqrt(dx * dx + dy * dy);
                return {
                    x: last.x + (first.x - last.x) * ratio,
                    y: last.y + (first.y - last.y) * ratio,
                    angle: last.angle + (first.angle - last.angle) * ratio
                };
            };
              // Generate bubble points
            const bubble_points = [];
            
            for (let i = 0; i < bubble_positions.length; i++) {
                const bubble = bubble_positions[i];
                
                // Base 1 position
                const base1_pos = get_position_at_distance(bubble.start);
                
                // Base 2 position - handle loop closure
                let base2_pos;
                if (bubble.closes_loop) {
                    // Use the first node's position as base2
                    base2_pos = get_position_at_distance(0);
                } else {
                    base2_pos = get_position_at_distance(bubble.end);
                }
                
                // Calculate the midpoint between the two bases in 2D space
                const mid_base_x = (base1_pos.x + base2_pos.x) / 2;
                const mid_base_y = (base1_pos.y + base2_pos.y) / 2;
                
                // Calculate the direction from base1 to base2
                const base_dx = base2_pos.x - base1_pos.x;
                const base_dy = base2_pos.y - base1_pos.y;
                const base_length = Math.sqrt(base_dx * base_dx + base_dy * base_dy);
                  // Perpendicular direction to the base line (pointing outward)
                const perp_x = base_dy / base_length;
                const perp_y = -base_dx / base_length;
                
                // High point at CONSTANT distance perpendicular from the midpoint
                const high_x = mid_base_x + perp_x * constant_mountain_height;
                const high_y = mid_base_y + perp_y * constant_mountain_height;
                
                // Add base1 node (corner)
                bubble_points.push({
                    x: base1_pos.x,
                    y: base1_pos.y,
                    type: 'main',
                    node_type: 'corner',
                    cp_in_x: base1_pos.x,
                    cp_in_y: base1_pos.y,
                    cp_out_x: base1_pos.x,
                    cp_out_y: base1_pos.y,
                    tangency_broken: false
                });
                
                // Calculate handle direction (perpendicular to the base line)
                const handle_length = bubble.width * 0.3;
                
                // Add high node (smooth with handles perpendicular to base line)
                bubble_points.push({
                    x: high_x,
                    y: high_y,
                    type: 'main',
                    node_type: 'smooth',
                    cp_in_x: high_x - base_dx / base_length * handle_length,
                    cp_in_y: high_y - base_dy / base_length * handle_length,
                    cp_out_x: high_x + base_dx / base_length * handle_length,
                    cp_out_y: high_y + base_dy / base_length * handle_length,
                    tangency_broken: false
                });
                
                // Only add base2 node if it's not the loop closure
                if (!bubble.closes_loop) {
                    bubble_points.push({
                        x: base2_pos.x,
                        y: base2_pos.y,
                        type: 'main',
                        node_type: 'corner',
                        cp_in_x: base2_pos.x,
                        cp_in_y: base2_pos.y,
                        cp_out_x: base2_pos.x,
                        cp_out_y: base2_pos.y,
                        tangency_broken: false
                    });
                }
            }
            
            return bubble_points;
        },

        /**
         * Regenerates thought balloon with current dimensions
         * @param {Object} balloon - The balloon to regenerate
         */
        regenerate_thought_balloon(balloon) {
            if (!balloon || balloon.getAttr('object_type') !== 'balloon') {
                console.warn('regenerate_thought_balloon called without valid balloon');
                return;
            }
            
            if (balloon.getAttr('balloon_type') !== 'thought') {
                console.warn('regenerate_thought_balloon called on non-thought balloon');
                return;
            }
            
            // Capture original balloon dimensions BEFORE any changes
            var original_balloon_bounds = balloon.getClientRect();
            var original_width = original_balloon_bounds.width;
            var original_height = original_balloon_bounds.height;
            
              // Always use current TRANSFORMED dimensions to regenerate at current size
            let current_width, current_height;
            
            if (this.transformer && this.transformer.nodes().includes(balloon)) {
                // Get dimensions from transformer which handles scaling correctly
                const transform_rect = this.transformer.getClientRect();
                current_width = transform_rect.width;
                current_height = transform_rect.height;
            } else {
                // Fallback to shape's current bounding box
                const shape_rect = balloon.shape.getClientRect({ relativeTo: balloon.getParent() });
                current_width = shape_rect.width;
                current_height = shape_rect.height;
            }
              // Generate new points with original dimensions to preserve size consistency
            const new_points = this.generate_adaptive_thought_balloon_points(current_width, current_height);
            const enriched_points = this.calculate_initial_control_points(new_points);
            
            // Update balloon with new shape
            balloon.setAttr('balloon_points', enriched_points);
            const new_path_data = this.generate_balloon_path_from_points(enriched_points);
            balloon.shape.data(new_path_data);
            
            // Apply distortion to make the balloon fit exactly in the original balloon area
            this.distort_balloon_to_fit(balloon, original_width, original_height);
            
            // If in edit mode, update handles
            if (balloon.getAttr('editing_mode')) {
                this.remove_balloon_edit_handles(balloon);
                this.create_balloon_edit_handles(balloon);
            }
            
            // Recenter text and update display
            this.recenter_balloon_text(balloon);
            this.redraw();
            this.push_to_history('regenerate_thought_balloon');
            this.status_message = 'Thought balloon pattern regenerated';
            
            // Refresh lucide icons
            this.$nextTick(() => {
                this.refresh_lucide();
            });
        },

        // Deterministic seeded random generator (LCG)
        seeded_random_factory(seed) {
            let s = typeof seed === 'number' ? seed : (typeof seed === 'string' ? seed.split('').reduce((a,c)=>a+c.charCodeAt(0),0) : Date.now());
            return function() {
                s = (s * 9301 + 49297) % 233280;
                return s / 233280;
            };
        },   
          
        update_balloon_shape_with_thought_bubbles(balloon) {
            if (!balloon || !balloon.shape) return;
            
            const thought_bubbles = balloon.getAttr('thought_bubbles') || [];
            
            // Remove existing thought bubble nodes
            this.remove_thought_bubble_nodes(balloon);
            
            if (thought_bubbles.length === 0) {
                // No thought bubbles, restore normal rendering
                const points = balloon.getAttr('balloon_points');
                if (points) {
                    const original_path = this.generate_balloon_path_from_points(points);
                    balloon.shape.data(original_path);
                }
                return;
            }
            
            // Generate original balloon path (main shape only)
            const points = balloon.getAttr('balloon_points');
            if (points) {
                const original_path = this.generate_balloon_path_from_points(points);
                balloon.shape.data(original_path);
            }
            
            // Create thought bubbles as separate Konva.Ellipse nodes
            this.create_thought_bubble_nodes(balloon, thought_bubbles);
        },

        create_thought_bubble_nodes(balloon, thought_bubbles) {
            if (!balloon || !thought_bubbles || thought_bubbles.length === 0) return;
            
            // Get the main shape's style properties
            const main_fill = balloon.shape.fill();
            const main_stroke = balloon.shape.stroke();
            const main_stroke_width = balloon.shape.strokeWidth();
            
            thought_bubbles.forEach((bubble_array, array_index) => {
                // Sort bubbles by radius (smallest first) so larger ones are drawn on top
                const sorted_bubbles = [...bubble_array].sort((a, b) => a.radius - b.radius);
                
                sorted_bubbles.forEach((bubble, bubble_index) => {
                    // Create ellipse node with separate X and Y radii for proper deformation
                    const ellipse = new Konva.Ellipse({
                        x: bubble.x,
                        y: bubble.y,
                        radiusX: bubble.radius_x || bubble.radius,
                        radiusY: bubble.radius_y || bubble.radius,
                        fill: main_fill,
                        stroke: main_stroke,
                        strokeWidth: main_stroke_width,
                        name: 'thought_bubble',
                        listening: false, // Don't interfere with balloon selection/editing
                        perfectDrawEnabled: false
                    });
                    
                    // Store reference data for updating
                    ellipse.setAttr('bubble_array_index', array_index);
                    ellipse.setAttr('bubble_index', bubble_index);
                    
                    // Add to balloon group - these will be automatically transformed with the balloon
                    balloon.add(ellipse);
                });
            });
        },
        
        remove_thought_bubble_nodes(balloon) {
            if (!balloon) return;
            
            // Find and remove all thought bubble nodes
            const thought_bubble_nodes = balloon.find('.thought_bubble');
            thought_bubble_nodes.forEach(node => node.destroy());
        },

        update_thought_bubble_styles(balloon) {
            if (!balloon || !balloon.shape) return;
            
            // Get current main shape styles
            const main_fill = balloon.shape.fill();
            const main_stroke = balloon.shape.stroke();
            const main_stroke_width = balloon.shape.strokeWidth();
            
            // Update all thought bubble nodes
            const thought_bubble_nodes = balloon.find('.thought_bubble');
            thought_bubble_nodes.forEach(node => {
                node.fill(main_fill);
                node.stroke(main_stroke);
                node.strokeWidth(main_stroke_width);
            });
        },

        initialize_existing_balloon_thought_bubbles(balloon) {
            if (!balloon || balloon.getAttr('object_type') !== 'balloon') return;
            
            const thought_bubbles = balloon.getAttr('thought_bubbles');
            if (thought_bubbles && thought_bubbles.length > 0) {
                // Remove any existing bubble nodes first
                this.remove_thought_bubble_nodes(balloon);
                // Create the bubble nodes
                this.create_thought_bubble_nodes(balloon, thought_bubbles);
            }
        },

        selected_balloon_has_multiple_texts() {
            return this.selected_object_is_balloon && 
                   this.selected_object.texts && 
                   Array.isArray(this.selected_object.texts) && 
                   this.selected_object.texts.length > 1;
        },

        get_page_balloon_count() {
            let total_balloons = 0;
            
            // Count balloons in all panels on current page
            this.panels.forEach(panel => {
                if (panel && panel.content_group) {
                    const balloons = panel.content_group.find('.panel_balloon');
                    total_balloons += balloons.length;
                }
            });
            
            return total_balloons;
        },
    },

};